/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.ignitor

import io.peekandpoke.klang.audio_be.AudioBuffer
import io.peekandpoke.klang.audio_be.utils.TWO_PI
import io.peekandpoke.klang.audio_be.utils.fastSin
import io.peekandpoke.klang.audio_be.utils.finiteOrZero
import io.peekandpoke.klang.audio_be.utils.rampStep
import io.peekandpoke.klang.audio_be.utils.wrapPhase
import io.peekandpoke.klang.audio_be.utils.wrapPhaseFastOrSafe
import io.peekandpoke.klang.audio_be.utils.wrapToUnitCycle
import io.peekandpoke.klang.audio_bridge.coerceUnisonVoices
import io.peekandpoke.klang.audio_bridge.constants.BROWN_LEAK_DEFAULT
import io.peekandpoke.klang.audio_bridge.constants.CRACKLE_C
import io.peekandpoke.klang.audio_bridge.constants.CRACKLE_CHAOS_DEFAULT
import io.peekandpoke.klang.audio_bridge.constants.CRACKLE_CHAOS_MAX
import io.peekandpoke.klang.audio_bridge.constants.CRACKLE_DC_POLE
import io.peekandpoke.klang.audio_bridge.constants.DUST_BIPOLAR_DEFAULT
import io.peekandpoke.klang.audio_bridge.constants.DUST_TAIL_DEFAULT
import io.peekandpoke.klang.audio_bridge.constants.NOISE_TILT_DEFAULT
import io.peekandpoke.klang.audio_bridge.constants.NOISE_TILT_LP_COEF
import io.peekandpoke.klang.audio_bridge.constants.PULSE_FALL_FLANK
import io.peekandpoke.klang.audio_bridge.constants.PULSE_MIN_FLANK_SAMPLES
import io.peekandpoke.klang.audio_bridge.constants.PULSE_RISE_FLANK
import io.peekandpoke.klang.audio_bridge.constants.RAMP_RESET_SAMPLES
import io.peekandpoke.klang.audio_bridge.constants.RAMP_SHAPE_MAX
import io.peekandpoke.klang.audio_bridge.constants.SAW_RESET_SAMPLES
import io.peekandpoke.klang.audio_bridge.constants.SAW_SHAPE_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_CENTER_JITTER_SCALE
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_DRAW_TRIES
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_GAIN_JITTER
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_K_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_K_MIN
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_PHASE_POOL
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_POOL_SIZE
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_REFRESH_EVERY
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_SELECTION
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_SIDE_ATTEN
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_SPREAD_POWER
import io.peekandpoke.klang.audio_bridge.constants.SUPERRAMP_WARMUP
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_CENTER_JITTER_SCALE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_DRAW_TRIES
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_GAIN_JITTER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_K_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_K_MIN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_PHASE_POOL
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_POOL_SIZE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_REFRESH_EVERY
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_SELECTION
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_SIDE_ATTEN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_SPREAD_POWER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSAW_WARMUP
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_CENTER_JITTER_SCALE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_DRAW_TRIES
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_GAIN_JITTER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_K_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_K_MIN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_PHASE_POOL
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_POOL_SIZE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_REFRESH_EVERY
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_SELECTION
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_SIDE_ATTEN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_SPREAD_POWER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSINE_WARMUP
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_CENTER_JITTER_SCALE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_DRAW_TRIES
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_GAIN_JITTER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_K_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_K_MIN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_PHASE_POOL
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_POOL_SIZE
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_REFRESH_EVERY
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_SELECTION
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_SIDE_ATTEN
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_SPREAD_POWER
import io.peekandpoke.klang.audio_bridge.constants.SUPERSQUARE_WARMUP
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_CENTER_JITTER_SCALE
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_DRAW_TRIES
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_GAIN_JITTER
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_K_MAX
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_K_MIN
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_PHASE_POOL
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_POOL_SIZE
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_REFRESH_EVERY
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_SELECTION
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_SIDE_ATTEN
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_SPREAD_POWER
import io.peekandpoke.klang.audio_bridge.constants.SUPERTRI_WARMUP
import io.peekandpoke.klang.common.math.BerlinNoise
import io.peekandpoke.klang.common.math.PerlinNoise
import io.peekandpoke.klang.common.math.semitones
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sign
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.random.Random

/**
 * Factory functions for Ignitor oscillator primitives.
 *
 * Each factory returns a fresh Ignitor with its own phase state.
 */
object Ignitors {

    // Shared ConstantIgnitor singletons used as factory defaults. The normal DSL
    // path (IgnitorDslRuntime.toExciter) always supplies an explicit runtime
    // ignitor, so these defaults only fire when a Kotlin caller invokes a factory
    // without that argument. Sprudel's ignitorParam lookup runs at the DSL layer
    // (IgnitorDslRuntime.buildIgnitor), upstream of these factories.
    private val analogDefault = ConstantIgnitor(0.0)
    private val analogSpreadDefault = ConstantIgnitor(1.0)
    private val voicesDefault = ConstantIgnitor(7.0)
    private val detuneDefault = ConstantIgnitor(0.2)
    private val dutyDefault = ConstantIgnitor(0.5)
    private val densityDefault = ConstantIgnitor(0.2)
    private val rateDefault = ConstantIgnitor(1.0)
    private val octavesDefault = ConstantIgnitor(1.0)
    private val persistenceDefault = ConstantIgnitor(0.5)
    private val chaosDefault = ConstantIgnitor(CRACKLE_CHAOS_DEFAULT)
    private val colorDefault = ConstantIgnitor(NOISE_TILT_DEFAULT)
    private val brownLeakDefault = ConstantIgnitor(BROWN_LEAK_DEFAULT)
    private val tailDefault = ConstantIgnitor(DUST_TAIL_DEFAULT)
    private val bipolarDefault = ConstantIgnitor(DUST_BIPOLAR_DEFAULT)
    private val feedbackDefault = ConstantIgnitor(0.996)
    private val brightnessDefault = ConstantIgnitor(0.5)
    private val pickPositionDefault = ConstantIgnitor(0.5)
    private val stiffnessDefault = ConstantIgnitor(0.0)

    /**
     * Sine wave oscillator. Inherently band-limited, no anti-aliasing needed. Phase 0 is `sin(0)`, the upward zero
     * crossing; [phase] (null: none) is the offset in cycles, see [PhaseOffset].
     */
    fun sine(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = SineIgnitor(freq = freq, analog = analog, phaseIn = phase?.let { PhaseOffset(it) })

    private class SineIgnitor(
        private val freq: Ignitor,
        private val analog: Ignitor,
        private val phaseIn: PhaseOffset?,
    ) : Ignitor {
        private var phase: Double = 0.0

        /** Built with the node, seeded at the first block (see [seedAnalogDrift]). */
        private val drift = AnalogDrift()
        private var driftSeeded: Boolean = false

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            if (!driftSeeded) {
                driftSeeded = true
                seedAnalogDrift(drift = drift, analog = analog, freqHz = actualFreq, ctx = ctx)
            }

            val d = drift
            val phaseInc = TWO_PI * actualFreq / ctx.sampleRateD
            val phaseMod = ctx.phaseMod
            val end = ctx.windowEnd

            if (phaseIn != null) {
                if (!phaseIn.isSignal) {
                    val shift = phaseIn.blockDelta(actualFreq)

                    if (shift != 0.0) {
                        phase = (phase + shift * TWO_PI).wrapPhase(TWO_PI)
                    }
                }

                if (phaseIn.isSignal) {
                    renderPhased(buffer = buffer, actualFreq = actualFreq, ctx = ctx, d = d, phaseInc = phaseInc, phaseIn = phaseIn)

                    return
                }
            }

            if (d.active) {
                d.beginBlock()

                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                var m = d.blockStart * 1.0
                val dm = rampStep(from = m, to = d.blockEnd, frames = end - ctx.offset)

                if (phaseMod == null) {
                    for (i in ctx.offset until end) {
                        buffer[i] = fastSin(phase)
                        phase += phaseInc * m
                        m += dm
                        phase = phase.wrapPhase(TWO_PI)
                    }
                } else {
                    for (i in ctx.offset until end) {
                        buffer[i] = fastSin(phase)
                        phase += phaseInc * phaseMod[i] * m
                        m += dm
                        phase = phase.wrapPhase(TWO_PI)
                    }
                }
            } else {
                if (phaseMod == null) {
                    for (i in ctx.offset until end) {
                        buffer[i] = fastSin(phase)
                        phase += phaseInc
                        phase = phase.wrapPhase(TWO_PI)
                    }
                } else {
                    for (i in ctx.offset until end) {
                        buffer[i] = fastSin(phase)
                        phase += phaseInc * phaseMod[i]
                        phase = phase.wrapPhase(TWO_PI)
                    }
                }
            }
        }

        /**
         * The phased loop (a moving `phase` signal): reads the sine at `phase + offset` and advances `phase` as the
         * four loops above do (the same products in the same order, so an offset of 0 everywhere renders what they
         * render).
         */
        private fun renderPhased(
            buffer: AudioBuffer, actualFreq: Double, ctx: IgniteContext, d: AnalogDrift, phaseInc: Double, phaseIn: PhaseOffset,
        ) {
            val phaseMod = ctx.phaseMod
            val off = ctx.offset
            val end = ctx.windowEnd

            ctx.scratchBuffers.use { offsets ->
                phaseIn.render(offsets, actualFreq, ctx)

                var m = 1.0
                var dm = 0.0

                if (d.active) {
                    d.beginBlock()
                    // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                    m = d.blockStart * 1.0
                    dm = rampStep(from = m, to = d.blockEnd, frames = end - off)
                }

                for (i in off until end) {
                    var p = phase + offsets[i].wrapToUnitCycle() * TWO_PI

                    if (p >= TWO_PI) {
                        p -= TWO_PI
                    }

                    buffer[i] = fastSin(p)

                    var inc = phaseInc

                    if (phaseMod != null) {
                        inc *= phaseMod[i]
                    }

                    phase += inc * m
                    m += dm
                    phase = phase.wrapPhase(TWO_PI)
                }
            }
        }
    }


    // ── Sine partial bank ────────────────────────────────────────────────────────

    /** Engine cap per bank (coerced, like every count): a runaway `count` signal cannot allocate without bound. */
    private const val PARTIAL_BANK_MAX_COUNT = 64

    /** The partial count [count] will read at the first block, as [unisonCapacity] reads a voice count. */
    private fun partialCapacity(count: Ignitor, countsAtBuild: Boolean): Int {
        if (!countsAtBuild) {
            return 0
        }

        val c = sizingValueOrNull(count) ?: return 0

        return c.toInt().coerceIn(0, PARTIAL_BANK_MAX_COUNT)
    }

    /**
     * A sine carrying banks of sine partials at multiples of its own frequency, rendered in ONE
     * block pass (KLANG-00185 (sine partial banks plan)). Replaces the hand-rolled
     * `Ignitor.sine().add(Ignitor.sine(freq * 2).mul(1/2)).add(...)` stacks: same partials, same gains,
     * one loop per partial instead of three block passes per partial.
     *
     * Partial 0 is the sine itself at gain [fundamental]. [harmonics] adds partials at `2f, 3f, ...`,
     * [octaves] at `2f, 4f, 8f, ...`, [suboctaves] at `f/2, f/4, ...`; an added partial at `m * f` or
     * `f / m` has gain `m ^ -rolloff` of its bank (the distance from the fundamental is `m` in both
     * directions). Banks sum without deduplication. Every knob is read once per block (control rate,
     * the `voices` pattern). Each bank owns its partial state, so a count change in one bank never
     * re-indexes another; surviving partials keep their phase, a partial that (re)activates starts
     * at the phase applied so far: at `phase` 0 (or 0.5) a zero crossing, so adds are step-free,
     * at any other `phase` a step of up to the partial's gain. Removals are not step-free: a count
     * decrement drops its partial at whatever phase it has (a step of up to that partial's gain),
     * so automate `rolloff`, not `count`, when it must move smoothly. A partial at or above Nyquist,
     * judged at block start on the base pitch, is silenced (gain 0); there is no lower limit.
     *
     * Drift: [analog] is the depth for every lane, latched at the first block like the plain sine.
     * [analogSpread] blends per partial between one SHARED walk (0: the bank wobbles as one physical
     * oscillator, the ratios stay exact) and the partial's OWN walk (1: independent
     * lanes, the beating of the hand-rolled stack), with constant-power weights `sqrt(1 - s)` and
     * `sqrt(s)` so the depth stays [analog] at every setting. The lanes live in one [DriftLanes]
     * and are drawn in a fixed order: one int for the shared lane's SEED at the first block, then
     * the fundamental's lane, then each bank's partials as the bank grows. The shared lane itself
     * runs on that seed and never draws from the voice rng, so a seeded voice renders reproducibly
     * whatever the spread does. A bank never retires a lane: a partial that drops out and comes back
     * restarts its phase at 0 but resumes the walk it had, which is inaudible at cent scale and
     * keeps a count sweep from re-seeding the whole spectrum.
     *
     * The per-sample loop mirrors [SineIgnitor] (radian phase, `fastSin(phase)`, `wrapPhase(TWO_PI)`),
     * so a bank of one partial with `analog = 0` is bit-identical to the plain sine. With drift they
     * part: the bank takes one extra int from the voice rng at its first block (its shared lane's
     * seed, taken whether or not the spread ever drops below 1), so its fundamental lane seeds from
     * a different point in the stream than the plain sine's lane does.
     *
     * [partials] is the fourth bank, explicit partials at any ratio (KLANG-00296 (sine inharmonic partials)):
     * its length is fixed for the note, so its storage and its drift lanes are built with the node and nothing grows
     * at render. Per block each partial reads its ratio and phase once (the bank knobs' rule): the frequency is
     * `ratio` times the sine's, and the partial is silent while its frequency's magnitude is at or above Nyquist, or
     * not finite; its phase is frozen meanwhile and it resumes where it stopped, at a step of up to its gain, the banks'
     * gate. A block-constant gain (a number, a slot) is read once per block, raw (a non-finite one reads as 0, as the
     * fundamental's does); a SIGNAL gain (an envelope, a tremolo) renders its block and is read per sample, so the
     * partial with an `adsr` gain renders what its own sine under the same `adsr` renders. The phase is a fraction of
     * one cycle: at the first block it is the start phase (the accumulator starts there); later a change glides
     * across its block, taken the short way round the cycle, as a frequency offset (no jump). Lanes: the explicit
     * partials take theirs after the three banks' at the first block, in list order. The caller passes at most
     * `SINE_MAX_PARTIALS` (`IgnitorDslRuntime` coerces the list).
     */
    fun sinePartials(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        fundamental: Ignitor = ConstantIgnitor(1.0),
        harmonics: Ignitor = ConstantIgnitor(0.0),
        harmonicsRolloff: Ignitor = ConstantIgnitor(1.0),
        octaves: Ignitor = ConstantIgnitor(0.0),
        octavesRolloff: Ignitor = ConstantIgnitor(1.0),
        suboctaves: Ignitor = ConstantIgnitor(0.0),
        suboctavesRolloff: Ignitor = ConstantIgnitor(1.0),
        analogSpread: Ignitor = ConstantIgnitor(1.0),
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
        partials: List<SinePartial> = emptyList(),
    ): Ignitor = PartialBankIgnitor(
        freq = freq, analog = analog, fundamental = fundamental,
        harmonics = harmonics, harmonicsRolloff = harmonicsRolloff, octaves = octaves, octavesRolloff = octavesRolloff, suboctaves = suboctaves, suboctavesRolloff = suboctavesRolloff,
        analogSpread = analogSpread, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
        partials = partials.toTypedArray(),
    )

    /** One explicit partial's built knobs for [sinePartials]: the runtime twin of `IgnitorDsl.Sine.Partial`. */
    class SinePartial(val ratio: Ignitor, val gain: Ignitor, val phase: Ignitor)

    private class PartialBankIgnitor(
        private val freq: Ignitor,
        private val analog: Ignitor,
        private val fundamental: Ignitor,
        private val harmonics: Ignitor,
        private val harmonicsRolloff: Ignitor,
        private val octaves: Ignitor,
        private val octavesRolloff: Ignitor,
        private val suboctaves: Ignitor,
        private val suboctavesRolloff: Ignitor,
        private val analogSpread: Ignitor,
        private val phaseIn: PhaseOffset?,
        countsAtBuild: Boolean,
        private val partials: Array<SinePartial>,
    ) : Ignitor {
        /**
         * One bank's partial state: growth-only arrays, a live [count], (re)activated partials restart at the applied
         * phase. The arrays are built with the node at [capacity], the count the build can read (see
         * [partialCapacity]); only a count signal past it grows them at render.
         */
        private class Bank(capacity: Int) {
            var count: Int = 0
            var phase: DoubleArray = DoubleArray(capacity)
            var gain: DoubleArray = DoubleArray(capacity)
            var inc: DoubleArray = DoubleArray(capacity)

            /** This bank's own drift lane per partial, as an index into the ignitor's [DriftLanes]. */
            var laneIdx: IntArray = IntArray(capacity)
            private var lanesAssigned: Int = 0

            /** [startPhase]: where a (re)activated partial starts, in radians (the offset a block-constant `phase` applied so far). */
            fun resize(newCount: Int, drift: DriftLanes?, startPhase: Double) {
                if (newCount > phase.size) {
                    phase = phase.copyOf(newCount)
                    gain = gain.copyOf(newCount)
                    inc = inc.copyOf(newCount)
                    laneIdx = laneIdx.copyOf(newCount)
                }

                // A partial keeps the lane it was handed for the whole note, so growth in ANOTHER
                // bank never re-indexes this one's walks (the lanes themselves never shrink).
                if (drift != null && newCount > lanesAssigned) {
                    val base = drift.laneCount

                    drift.ensureLanes(base + (newCount - lanesAssigned))

                    for (i in lanesAssigned until newCount) {
                        laneIdx[i] = base + (i - lanesAssigned)
                    }

                    lanesAssigned = newCount
                }

                for (i in count until newCount) {
                    phase[i] = startPhase // at phase 0 or 0.5 a zero crossing (step-free), elsewhere a step
                }

                count = newCount
            }
        }

        private var fundPhase: Double = 0.0
        private var fundGain: Double = 0.0
        private var fundInc: Double = 0.0

        private val harmonicsBank = Bank(capacity = partialCapacity(count = harmonics, countsAtBuild = countsAtBuild))
        private val octavesBank = Bank(capacity = partialCapacity(count = octaves, countsAtBuild = countsAtBuild))
        private val suboctavesBank = Bank(capacity = partialCapacity(count = suboctaves, countsAtBuild = countsAtBuild))

        /** The explicit partials: a bank whose count is the list's, fixed for the note, so it is built at its size. */
        private val partialsBank = Bank(capacity = partials.size)

        /** Each explicit partial's phase knob already folded into its accumulator, in cycles, wrapped (0 before the first block). */
        private val partialApplied = DoubleArray(partials.size)

        /**
         * Each explicit partial's gain when it is a SIGNAL (not block-constant: an envelope, a tremolo), else null. A
         * signal gain renders its block once per block and is read per sample ([renderPartialGained]), so an envelope
         * keeps its attack and a tremolo stays smooth; a block-constant gain is read once per block like every bank knob.
         */
        private val gainSignals: Array<Ignitor?> = Array(partials.size) { i -> partials[i].gain.takeIf { !it.isBlockConstant } }

        /** False until the first block has read the explicit partials (that block's phase change is the start phase, a jump). */
        private var explicitStarted: Boolean = false
        private val banks = arrayOf(harmonicsBank, octavesBank, suboctavesBank, partialsBank)

        /** The banks [renderPartials] walks generically; the explicit bank renders in its own loop after them. */
        private val countedBanks = arrayOf(harmonicsBank, octavesBank, suboctavesBank)

        /** Drift depth, latched at the first block like the plain sine; a NaN read latches as inactive. */
        private var analogAmt: Double = 0.0
        private var latched: Boolean = false

        /**
         * Lane 0 is the fundamental; each bank's partials take the lanes after it as they grow. Built with the node when
         * `analog` may be above 0, one lane per partial the build can count (see [buildDriftLanes]); [drift] is the
         * container once started at the first block, null while `analog` is 0.
         */
        private val builtLanes: DriftLanes? = buildDriftLanes(
            analog = analog,
            analogSpread = analogSpread,
            lanes = 1 + harmonicsBank.phase.size + octavesBank.phase.size + suboctavesBank.phase.size + partialsBank.phase.size,
        )
        private var drift: DriftLanes? = null

        /** Coerces a count signal to `0..PARTIAL_BANK_MAX_COUNT`; NaN reads as 0. */
        private fun count(param: Ignitor, freqHz: Double, ctx: IgniteContext): Int =
            readParam(param, freqHz, ctx).toInt().coerceIn(0, PARTIAL_BANK_MAX_COUNT)

        /** `m ^ -rolloff`, 0 when the law overflows (a huge negative rolloff); NaN-guard: never NaN. */
        private fun law(m: Double, rolloff: Double): Double {
            val g = m.pow(-rolloff)
            return g.finiteOrZero()
        }

        /**
         * Gain of a partial at [partialFreq], the one Nyquist law of the node (the fundamental and all four banks): [g]
         * while the frequency's MAGNITUDE is below Nyquist, else 0. A negative frequency (a negative base, a negative
         * ratio) aliases like a positive one, and the comparison is written so a NaN frequency fails it: silent, never NaN.
         */
        private fun gate(partialFreq: Double, g: Double, ctx: IgniteContext): Double =
            if (abs(partialFreq) < ctx.sampleRateD * 0.5) g else 0.0

        /**
         * Renders one partial into [buffer] (writes when [first], adds otherwise) and returns its new
         * phase. One tight loop per partial with the phase in a local: the shape the JIT renders
         * fastest (see the sine stack; a sample-major loop over phase arrays measured 2x slower).
         */
        private fun renderPartial(
            buffer: AudioBuffer, off: Int, end: Int, first: Boolean,
            phaseIn: Double, d: Double, g: Double,
            pm: DoubleArray?, drift: DriftLanes?, lane: Int,
        ): Double {
            // `* 1.0`: an argument that seeds a loop-carried phase must pass an arithmetic op on V8, or every sample
            // boxes it: about 10 KB per block for a five-partial bank with drift, on both bundles, pinned to one core
            // and not (audio/ref/performance.md, V8 allocation pass).
            var ph = phaseIn * 1.0
            // The drift ramp for this partial and block: one add per sample (DriftLanes KDoc).
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(lane)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                val s = g * fastSin(ph)
                buffer[i] = if (first) s else buffer[i] + s

                var step = d * m

                if (pm != null) {
                    step *= pm[i]
                }

                m += dm
                ph = (ph + step).wrapPhase(TWO_PI)
            }

            return ph
        }

        /**
         * [renderPartial] for a moving `phase` signal: every partial reads at its own `phase + offset` (each shifted
         * by the same fraction of its OWN cycle) and advances exactly as [renderPartial] advances it.
         */
        private fun renderPartialPhased(
            buffer: AudioBuffer, off: Int, end: Int, first: Boolean,
            phaseIn: Double, d: Double, g: Double,
            pm: DoubleArray?, drift: DriftLanes?, lane: Int, offsets: AudioBuffer,
        ): Double {
            // `* 1.0`: an argument that seeds a loop-carried phase must pass an arithmetic op on V8, or every sample
            // boxes it: about 10 KB per block for a five-partial bank with drift, on both bundles, pinned to one core
            // and not (audio/ref/performance.md, V8 allocation pass).
            var ph = phaseIn * 1.0
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(lane)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                var p = ph + offsets[i].wrapToUnitCycle() * TWO_PI

                if (p >= TWO_PI) {
                    p -= TWO_PI
                }

                val s = g * fastSin(p)
                buffer[i] = if (first) s else buffer[i] + s

                var step = d * m

                if (pm != null) {
                    step *= pm[i]
                }

                m += dm
                ph = (ph + step).wrapPhase(TWO_PI)
            }

            return ph
        }

        /**
         * [renderPartial] and [renderPartialPhased] for explicit partial [index] whose gain is a signal: the gain is read
         * per sample from [gains] (this block's render of it; a non-finite sample reads as 0). The phase advances exactly
         * as in the two loops above, so a gain that holds a constant value renders what they render. The phase is read from
         * and written back to the bank's arrays, the increment read from them, not handed over as doubles: on V8 a double
         * argument or result of a call it does not inline is a heap number (audio/ref/performance.md).
         */
        private fun renderPartialGained(
            buffer: AudioBuffer, off: Int, end: Int, first: Boolean, index: Int, gains: AudioBuffer,
            pm: DoubleArray?, drift: DriftLanes?, offsets: AudioBuffer?,
        ) {
            val bank = partialsBank
            val d = bank.inc[index]
            val lane = bank.laneIdx[index]
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop boxes it (audio/ref/performance.md).
            var ph = bank.phase[index] * 1.0
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(lane)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                var p = ph

                if (offsets != null) {
                    p = ph + offsets[i].wrapToUnitCycle() * TWO_PI

                    if (p >= TWO_PI) {
                        p -= TWO_PI
                    }
                }

                // NaN-guard: a non-finite gain sample reads as 0 (finiteOrZero), as a block-constant gain does.
                val s = gains[i].finiteOrZero() * fastSin(p)
                buffer[i] = if (first) s else buffer[i] + s

                var step = d * m

                if (pm != null) {
                    step *= pm[i]
                }

                m += dm
                ph = (ph + step).wrapPhase(TWO_PI)
            }

            bank.phase[index] = ph
        }

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            if (!latched) {
                latched = true
                analogAmt = readParam(analog, actualFreq, ctx)

                if (analogAmt > 0.0) {
                    // Lane 0 is the fundamental, drawn here so it keeps the head of the draw order.
                    val lanes = startDriftLanes(built = builtLanes, analog = analogAmt, ctx = ctx)

                    lanes.ensureLanes(1)
                    drift = lanes
                }
            }

            val h = count(harmonics, actualFreq, ctx)
            val o = count(octaves, actualFreq, ctx)
            val sub = count(suboctaves, actualFreq, ctx)
            val lanes = drift
            // A block-constant phase is folded into every accumulator, so a partial that (re)activates starts at the
            // offset applied so far (0 without a phase: the step-free zero crossing); this block's change follows below.
            val startPhase = if (phaseIn != null && !phaseIn.isSignal) phaseIn.applied * TWO_PI else 0.0

            harmonicsBank.resize(h, lanes, startPhase)
            octavesBank.resize(o, lanes, startPhase)
            suboctavesBank.resize(sub, lanes, startPhase)
            // The explicit partials activate at the first block, after the banks (their lanes follow the banks'), and never change count.
            partialsBank.resize(partials.size, lanes, startPhase)

            val baseInc = TWO_PI * actualFreq / ctx.sampleRateD
            val fund = readParam(fundamental, actualFreq, ctx)
            // NaN-guard: a non-finite fundamental gain reads as 0 (the hand-rolled `.mul(gain)` scrubbed NaN too).
            fundGain = gate(partialFreq = actualFreq, g = fund.finiteOrZero(), ctx = ctx)
            fundInc = baseInc

            val rh = readParam(harmonicsRolloff, actualFreq, ctx)
            val ro = readParam(octavesRolloff, actualFreq, ctx)
            val rs = readParam(suboctavesRolloff, actualFreq, ctx)

            for (i in 0 until h) {
                val m = (2 + i).toDouble()
                harmonicsBank.gain[i] = gate(partialFreq = m * actualFreq, g = law(m = m, rolloff = rh), ctx = ctx)
                harmonicsBank.inc[i] = TWO_PI * (m * actualFreq) / ctx.sampleRateD
            }

            var m = 1.0

            for (i in 0 until o) {
                m *= 2.0
                octavesBank.gain[i] = gate(partialFreq = m * actualFreq, g = law(m = m, rolloff = ro), ctx = ctx)
                octavesBank.inc[i] = TWO_PI * (m * actualFreq) / ctx.sampleRateD
            }

            m = 1.0

            for (i in 0 until sub) {
                m *= 2.0
                val partialFreq = actualFreq / m
                suboctavesBank.gain[i] = gate(partialFreq = partialFreq, g = law(m = m, rolloff = rs), ctx = ctx)
                suboctavesBank.inc[i] = TWO_PI * partialFreq / ctx.sampleRateD
            }

            readExplicitPartials(actualFreq = actualFreq, ctx = ctx)

            val pm = ctx.phaseMod
            val off = ctx.offset
            val end = ctx.windowEnd

            // Read outside the null check: a modulated subtree advances once per block whether or
            // not anything reads its value this block (ledger O2).
            val analogSpreadAmt = readParam(analogSpread, actualFreq, ctx)

            // The shared walk steps once for the whole bank, BEFORE the partials, so every
            // partial's ramp blends the same shared move.
            if (lanes != null) {
                lanes.prepareBlock(analogSpreadAmt)
            }

            if (phaseIn != null) {
                if (!phaseIn.isSignal) {
                    shiftPartials(phaseIn.blockDelta(actualFreq))
                }

                if (phaseIn.isSignal) {
                    ctx.scratchBuffers.use { offsets ->
                        phaseIn.render(offsets, actualFreq, ctx)
                        renderPartials(buffer = buffer, off = off, end = end, pm = pm, lanes = lanes, offsets = offsets, actualFreq = actualFreq, ctx = ctx)
                    }

                    return
                }
            }

            renderPartials(buffer = buffer, off = off, end = end, pm = pm, lanes = lanes, offsets = null, actualFreq = actualFreq, ctx = ctx)
        }

        /**
         * Reads every explicit partial's three knobs for this block: its gain and increment, and its phase change folded
         * into its accumulator (moved even while the partial is silent, so the phase it resumes at is the one written).
         */
        private fun readExplicitPartials(actualFreq: Double, ctx: IgniteContext) {
            val bank = partialsBank

            val frames = ctx.windowEnd - ctx.offset
            // The first block's phase change is the start phase, a jump of the accumulator; later changes glide (below).
            val glide = explicitStarted && frames > 0

            explicitStarted = true

            for (i in partials.indices) {
                val p = partials[i]
                val partialFreq = readParam(p.ratio, actualFreq, ctx) * actualFreq
                // NaN-guard: a non-finite gain reads as 0, as the fundamental's does. A signal gain is not read here: it
                // renders its block in renderPartials and is read per sample; 1 only marks it as sounding.
                val g = if (gainSignals[i] == null) readParam(p.gain, actualFreq, ctx).finiteOrZero() else 1.0
                // A non-finite phase reads as 0 (wrapToUnitCycle).
                val ph = readParam(p.phase, actualFreq, ctx).wrapToUnitCycle()
                var delta = ph - partialApplied[i]
                var inc = TWO_PI * partialFreq / ctx.sampleRateD
                val gain = gate(partialFreq = partialFreq, g = g, ctx = ctx)

                if (delta != 0.0) {
                    partialApplied[i] = ph

                    // A silent partial does not render, so its change is a jump (inaudible), not a glide it would lose.
                    if (glide && gain != 0.0) {
                        // A moving phase: the change, taken the short way round the cycle, is spread over this block as a
                        // frequency offset, so the partial arrives at the new phase at the block's end without a jump
                        // (exact without drift and phase modulation, which scale the glide as they scale the increment).
                        if (delta > 0.5) {
                            delta -= 1.0
                        } else if (delta <= -0.5) {
                            delta += 1.0
                        }

                        inc += delta * TWO_PI / frames
                    } else {
                        bank.phase[i] = (bank.phase[i] + delta * TWO_PI).wrapPhase(TWO_PI)
                    }
                }

                bank.gain[i] = gain
                bank.inc[i] = inc
            }
        }

        /** Moves every live accumulator by a block-constant phase change of [shift] cycles (0: nothing moves). */
        private fun shiftPartials(shift: Double) {
            if (shift == 0.0) {
                return
            }

            val rad = shift * TWO_PI

            fundPhase = (fundPhase + rad).wrapPhase(TWO_PI)

            for (bank in banks) {
                for (i in 0 until bank.count) {
                    bank.phase[i] = (bank.phase[i] + rad).wrapPhase(TWO_PI)
                }
            }
        }

        /** Renders the fundamental and every bank; [offsets] is a moving `phase` signal's block, or null. */
        private fun renderPartials(
            buffer: AudioBuffer, off: Int, end: Int, pm: DoubleArray?, lanes: DriftLanes?, offsets: AudioBuffer?,
            actualFreq: Double, ctx: IgniteContext,
        ) {
            var first = true

            if (fundGain != 0.0) {
                fundPhase = if (offsets == null) {
                    renderPartial(buffer = buffer, off = off, end = end, first = first, phaseIn = fundPhase, d = fundInc, g = fundGain, pm = pm, drift = lanes, lane = 0)
                } else {
                    renderPartialPhased(buffer = buffer, off = off, end = end, first = first, phaseIn = fundPhase, d = fundInc, g = fundGain, pm = pm, drift = lanes, lane = 0, offsets = offsets)
                }
                first = false
            }

            for (bank in countedBanks) {
                for (i in 0 until bank.count) {
                    val g = bank.gain[i]

                    if (g == 0.0) {
                        continue // silent (Nyquist): nothing to add, nothing to advance
                    }

                    bank.phase[i] = if (offsets == null) {
                        renderPartial(buffer = buffer, off = off, end = end, first = first, phaseIn = bank.phase[i], d = bank.inc[i], g = g, pm = pm, drift = lanes, lane = bank.laneIdx[i])
                    } else {
                        renderPartialPhased(
                            buffer = buffer, off = off, end = end, first = first, phaseIn = bank.phase[i], d = bank.inc[i], g = g, pm = pm, drift = lanes, lane = bank.laneIdx[i], offsets = offsets,
                        )
                    }
                    first = false
                }
            }

            val pb = partialsBank

            for (i in 0 until pb.count) {
                val g = pb.gain[i]
                val gainSignal = gainSignals[i]

                if (gainSignal == null) {
                    if (g == 0.0) {
                        continue // silent (a gain of 0, or Nyquist): nothing to add, nothing to advance
                    }

                    pb.phase[i] = if (offsets == null) {
                        renderPartial(buffer = buffer, off = off, end = end, first = first, phaseIn = pb.phase[i], d = pb.inc[i], g = g, pm = pm, drift = lanes, lane = pb.laneIdx[i])
                    } else {
                        renderPartialPhased(
                            buffer = buffer, off = off, end = end, first = first, phaseIn = pb.phase[i], d = pb.inc[i], g = g, pm = pm, drift = lanes, lane = pb.laneIdx[i], offsets = offsets,
                        )
                    }
                    first = false

                    continue
                }

                // A signal gain renders its block every block, sounding or not (a modulated subtree advances once per block).
                ctx.scratchBuffers.use { gains ->
                    gainSignal.generate(gains, actualFreq, ctx)

                    if (g != 0.0) {
                        renderPartialGained(buffer = buffer, off = off, end = end, first = first, index = i, gains = gains, pm = pm, drift = lanes, offsets = offsets)
                        first = false
                    }
                }
            }

            if (first) {
                buffer.fill(0.0, off, end)
            }
        }
    }

    // ── One waveform engine ──────────────────────────────────────────────────────
    // saw / ramp / square / pulse / triangle (+ raw zaw / zamp / pulze) all render through the SAME
    // shape ([waveTrapezoid] via [WaveVoiceState]) and the same hot loop — see [WaveIgnitor].

    private enum class WaveKind { SAW, PULSE }

    /**
     * The single mono oscillator behind saw / ramp / square / pulse / triangle (and the raw
     * zaw / zamp / pulze). A `±1` piecewise-linear [waveTrapezoid] in a [WaveVoiceState], finite-slope
     * edges (no PolyBLEP). [kind] picks the shape config (SAW = rise + finite flyback; PULSE = min-flank
     * rise/fall around a high plateau of width [duty]). [polarity] negates for ramp/zamp. [flankSamples]
     * is the edge length in samples (`0` = raw / instant / aliased). [duty] may be audio-rate (PWM).
     */
    private class WaveIgnitor(
        private val freq: Ignitor,
        private val analog: Ignitor,
        private val kind: WaveKind,
        private val polarity: Double,
        private val flankSamples: Double,
        private val duty: Ignitor = ConstantIgnitor(0.5),
        private val riseFlank: Double = 0.0,
        private val fallFlank: Double = 0.0,
        // SAW-only: caps the saw flyback fraction; read solely in the WaveKind.SAW branch. The PULSE kind
        // (square/triangle) ignores it — no per-shape WaveIgnitor split needed since the DSL types already
        // separate SAW (Saw/Ramp expose shapeMax) from PULSE (Pulze/Tri don't).
        private val shapeMax: Double = SAW_SHAPE_MAX,
        private val phaseIn: PhaseOffset? = null,
    ) : Ignitor {
        private val voice = WaveVoiceState()

        /**
         * The drift lane, built with the node when `analog` may be above 0 ([mayDrift]); seeded and handed to [voice] at
         * the first block when `analog` is above 0, built there if the build ruled it out.
         */
        private val builtLane: AnalogDrift? = if (mayDrift(analog)) AnalogDrift() else null
        private var driftInit = false
        private var lastDt: Double = Double.NaN
        private var lastDuty: Double = Double.NaN

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            if (!driftInit) {
                driftInit = true

                val amt = readParam(analog, actualFreq, ctx)

                if (amt > 0.0) {
                    val lane = builtLane ?: AnalogDrift()

                    lane.seed(analog = amt, stepRate = ctx.driftStepRate, rng = ctx.random)
                    voice.drift = lane
                } else {
                    voice.drift = null
                }
            }

            val dt = actualFreq / ctx.sampleRateD
            // The loop methods read `dt` from the holder, never as an argument: both are past V8's 460-byte inlining
            // limit, and a non-integral double handed to a call V8 does not inline is a heap number per call, one per
            // block per oscillator (audio/ref/performance.md, engine follow-up 6). No test can pin it: this is the guard.
            voice.dt = dt
            val pm = ctx.phaseMod
            // Same hoist as the unison stacks (see UnisonStackIgnitor.trapezoidLoop): the
            // one-subtract wrap holds only while |inc| < 1. Past that a positive dt parks the
            // trapezoid on its low plateau and a negative one rides the rise ramp without bound.
            val safeWrap = pm != null || voice.drift != null || !(abs(dt) < 1.0)
            val off = ctx.offset
            val end = off + ctx.length
            // A block-constant phase moves the accumulator once here; a moving one renders through the phased loop.
            if (phaseIn != null && !phaseIn.isSignal) {
                val shift = phaseIn.blockDelta(actualFreq)

                if (shift != 0.0) {
                    voice.phase = (voice.phase + shift).wrapPhase(1.0)
                }
            }

            val phased = phaseIn?.takeIf { it.isSignal }

            if (kind == WaveKind.SAW) {
                if (dt != lastDt) {
                    lastDt = dt

                    val rf = (flankSamples * dt).coerceAtMost(shapeMax)

                    // NaN-guard: a NaN frequency made the flyback NaN and every sample NaN (the trapezoid's fall
                    // branch). The phase is scrubbed to 0 by the wrap meanwhile, so any finite shape holds -1: a voice at a NaN
                    // frequency now carries a held -1 (DC through its envelope) where it used to be NaN, scrubbed to silence.
                    voice.setSawShape(if (rf != rf) shapeMax else rf)
                }

                if (phased != null) {
                    renderPhased(buffer = buffer, off = off, end = end, pm = pm, safeWrap = safeWrap, phaseIn = phased, actualFreq = actualFreq, ctx = ctx, dutyBuf = null)
                } else {
                    renderHoisted(buffer = buffer, off = off, end = end, pm = pm, safeWrap = safeWrap)
                }

                return
            }

            // PULSE — block-constant duty: bake once + hoist; audio-rate duty (PWM): rebake per
            // sample. The structural flag selects the branch, so the PWM path pays one boolean read per block.
            if (duty.isBlockConstant) {
                val d = duty.controlRateValue(actualFreq)

                if (d != lastDuty || dt != lastDt) {
                    lastDuty = d; lastDt = dt
                    voice.setPulseShape(duty = d, riseFlank = riseFlank, fallFlank = fallFlank, floor = flankSamples * dt)
                }

                if (phased != null) {
                    renderPhased(buffer = buffer, off = off, end = end, pm = pm, safeWrap = safeWrap, phaseIn = phased, actualFreq = actualFreq, ctx = ctx, dutyBuf = null)
                } else {
                    renderHoisted(buffer = buffer, off = off, end = end, pm = pm, safeWrap = safeWrap)
                }
            } else {
                if (dt != lastDt) {
                    lastDt = dt; lastDuty = Double.NaN
                }

                val floor = flankSamples * dt

                ctx.scratchBuffers.use { dutyBuf ->
                    duty.generate(dutyBuf, actualFreq, ctx)

                    if (phased != null) {
                        renderPhased(buffer = buffer, off = off, end = end, pm = pm, safeWrap = safeWrap, phaseIn = phased, actualFreq = actualFreq, ctx = ctx, dutyBuf = dutyBuf)

                        return
                    }

                    // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop boxes the phase per sample: about
                    // 2 KB per block on the development bundle under a mixed profile (audio/ref/performance.md, engine follow-up 5).
                    var phase = voice.phase * 1.0
                    val drift = voice.drift
                    val pol = polarity
                    var m = 1.0
                    var dm = 0.0

                    if (drift != null) {
                        drift.beginBlock()
                        // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                        m = drift.blockStart * 1.0
                        dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
                    }

                    for (i in off until end) {
                        val d = dutyBuf[i]

                        if (d != lastDuty) {
                            lastDuty = d; voice.setPulseShape(duty = d, riseFlank = riseFlank, fallFlank = fallFlank, floor = floor)
                        }

                        buffer[i] = pol * waveTrapezoid(
                            p = phase, riseEnd = voice.riseEnd, highEnd = voice.highEnd, fallEnd = voice.fallEnd, riseSlope = voice.riseSlope, fallSlope = voice.fallSlope,
                        )

                        var inc = dt * m

                        if (pm != null) {
                            inc *= pm[i]
                        }

                        m += dm
                        phase += inc
                        phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
                    }

                    voice.phase = phase
                }
            }
        }

        /** Tight loop with the shape hoisted into locals (constant within the block); `dt` is the holder's [WaveVoiceState.dt]. */
        private fun renderHoisted(
            buffer: AudioBuffer, off: Int, end: Int, pm: DoubleArray?, safeWrap: Boolean,
        ) {
            val dt = voice.dt
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
            var phase = voice.phase * 1.0
            val drift = voice.drift
            val pol = polarity
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.beginBlock()
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            val riseEnd = voice.riseEnd
            val highEnd = voice.highEnd
            val fallEnd = voice.fallEnd
            val riseSlope = voice.riseSlope
            val fallSlope = voice.fallSlope

            for (i in off until end) {
                buffer[i] = pol * waveTrapezoid(p = phase, riseEnd = riseEnd, highEnd = highEnd, fallEnd = fallEnd, riseSlope = riseSlope, fallSlope = fallSlope)

                var inc = dt * m

                if (pm != null) {
                    inc *= pm[i]
                }

                m += dm
                phase += inc
                phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
            }

            voice.phase = phase
        }

        /**
         * The phased loop (a moving `phase` signal): reads the trapezoid at `phase + offset`, advances `phase` as
         * [renderHoisted] and the PWM loop do; `dt` is the holder's [WaveVoiceState.dt]. [dutyBuf] is the PWM block (the
         * shape rebaked per sample on the flank floor `flankSamples * dt`, as the PWM loop does), or null for the shape
         * already baked for this block. The floor is computed here, not handed in, for the holder's reason (see `generate`).
         */
        private fun renderPhased(
            buffer: AudioBuffer, off: Int, end: Int, pm: DoubleArray?, safeWrap: Boolean,
            phaseIn: PhaseOffset, actualFreq: Double, ctx: IgniteContext, dutyBuf: AudioBuffer?,
        ) {
            val dt = voice.dt
            val floor = flankSamples * dt

            ctx.scratchBuffers.use { offsets ->
                phaseIn.render(offsets, actualFreq, ctx)

                // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
                var phase = voice.phase * 1.0
                val drift = voice.drift
                val pol = polarity
                var m = 1.0
                var dm = 0.0

                if (drift != null) {
                    drift.beginBlock()
                    // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                    m = drift.blockStart * 1.0
                    dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
                }

                for (i in off until end) {
                    if (dutyBuf != null) {
                        val d = dutyBuf[i]

                        if (d != lastDuty) {
                            lastDuty = d; voice.setPulseShape(duty = d, riseFlank = riseFlank, fallFlank = fallFlank, floor = floor)
                        }
                    }

                    var p = phase + offsets[i].wrapToUnitCycle()

                    if (p >= 1.0) {
                        p -= 1.0
                    }

                    buffer[i] = pol * waveTrapezoid(
                        p = p, riseEnd = voice.riseEnd, highEnd = voice.highEnd, fallEnd = voice.fallEnd, riseSlope = voice.riseSlope, fallSlope = voice.fallSlope,
                    )

                    var inc = dt * m

                    if (pm != null) {
                        inc *= pm[i]
                    }

                    m += dm
                    phase += inc
                    phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
                }

                voice.phase = phase
            }
        }
    }

    /**
     * Sawtooth: rise then a finite flyback ([SAW_RESET_SAMPLES] samples; no PolyBLEP, softens with
     * pitch). Single-voice form of the shape shared with [superSaw]. Per-voice analog drift via [analog].
     * Phase 0 is -1, the bottom of the rise (the flyback ends the cycle); [phase] (null: none) see [PhaseOffset].
     */
    fun saw(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        resetSamples: Double = SAW_RESET_SAMPLES,
        shapeMax: Double = SAW_SHAPE_MAX,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.SAW, polarity = 1.0, flankSamples = resetSamples, shapeMax = shapeMax,
        phaseIn = phase?.let { PhaseOffset(it) },
    )

    /** Reverse sawtooth: the negated [saw] (own `RAMP_RESET_SAMPLES` flyback knob). Phase 0 is +1, the top of the fall. */
    fun ramp(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        resetSamples: Double = RAMP_RESET_SAMPLES,
        shapeMax: Double = RAMP_SHAPE_MAX,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.SAW, polarity = -1.0, flankSamples = resetSamples, shapeMax = shapeMax,
        phaseIn = phase?.let { PhaseOffset(it) },
    )

    /** Square wave — a 50%-duty [pulze] (Kotlin convenience; the DSL drives `duty` via an Ignitor slot). */
    fun square(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = pulze(freq = freq, duty = ConstantIgnitor(0.5), analog = analog, phase = phase)

    /** Triangle wave: the pulse engine with both flanks fully open (duty 0.5, rise = fall = 1). Phase 0 is -1, its lowest point. */
    fun tri(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.PULSE, polarity = 1.0, flankSamples = PULSE_MIN_FLANK_SAMPLES,
        duty = ConstantIgnitor(0.5), riseFlank = 1.0, fallFlank = 1.0, phaseIn = phase?.let { PhaseOffset(it) },
    )

    /** White noise generator. Flat spectrum with equal energy at all frequencies. */
    /**
     * White noise with an optional first-order spectral tilt ([color]). `color == 0` → flat white with
     * the filter bypassed (the per-sample cost is exactly today's pure-rng loop — perf-neutral default).
     * `color < 0` crossfades toward a one-pole LP (darken); `color > 0` toward the complementary HP
     * (brighten). [color] is read control-rate (once per block).
     */
    fun whiteNoise(rng: Random, color: Ignitor = colorDefault): Ignitor = WhiteNoiseIgnitor(rng, color)

    private class WhiteNoiseIgnitor(private val rng: Random, private val color: Ignitor) : Ignitor {
        private var lp = 0.0 // one-pole LP state for the tilt (persists across blocks)

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            // control-rate read with no buffer fill for Constant/Param color → perf-neutral default.
            // The REAL freqHz on purpose (ledger O6): 0.0 made Ignitor.freq() inside noise params read
            // 0 Hz, and split the MemoizingIgnitor key for a node shared with the signal spine
            // (the shared node then ran twice per block).
            val c = readParam(color, freqHz, ctx).coerceIn(-1.0, 1.0)
            val end = ctx.windowEnd

            if (c == 0.0) {
                for (i in ctx.offset until end) {
                    buffer[i] = (rng.nextDouble() * 2.0 - 1.0)
                }
            } else {
                val darken = if (c < 0.0) -c else 0.0 // crossfade white→lp
                val brighten = if (c > 0.0) c else 0.0 // crossfade white→hp

                for (i in ctx.offset until end) {
                    val white = rng.nextDouble() * 2.0 - 1.0

                    lp += NOISE_TILT_LP_COEF * (white - lp)

                    val hp = white - lp

                    buffer[i] = white + darken * (lp - white) + brighten * (hp - white)
                }
            }
        }
    }

    /** Naive sawtooth ("zaw"): the raw [saw] (`flankSamples = 0` → instant reset, aliased/harsh). Phase 0 is -1. */
    fun zawtooth(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.SAW, polarity = 1.0, flankSamples = 0.0, phaseIn = phase?.let { PhaseOffset(it) },
    )

    /** Raw ramp ("zamp"): the negated [zawtooth] (naive reverse saw, no anti-aliasing). Phase 0 is +1. */
    fun zamp(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.SAW, polarity = -1.0, flankSamples = 0.0, phaseIn = phase?.let { PhaseOffset(it) },
    )

    /**
     * Raw pulse ("pulze"): naive aliased pulse with variable [duty] (the raw [square]). Phase 0 is +1, the start of
     * the high plateau: the instant rising edge sits at the wrap (high until `duty`, then low). The rounded [pulze]
     * starts on the other side of its rising edge, at -1.
     */
    fun rawPulze(
        freq: Ignitor = FreqIgnitor,
        duty: Ignitor = dutyDefault,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.PULSE, polarity = 1.0, flankSamples = 0.0,
        duty = duty, riseFlank = PULSE_RISE_FLANK, fallFlank = PULSE_FALL_FLANK, phaseIn = phase?.let { PhaseOffset(it) },
    )

    /**
     * Impulse: outputs 1.0 once per cycle (at phase wrap), 0.0 otherwise. Phase 0 is the spike: the first sample is
     * 1. With [phase] (null: none, see [PhaseOffset]) the spike sits where the shifted phase passes 0 going forward,
     * so a start phase of 0.25 spikes first after three quarters of a cycle, and a moving phase moves the spikes
     * (one per forward crossing, a step back of less than half a cycle spikes nothing).
     */
    fun impulse(
        freq: Ignitor = FreqIgnitor,
        analog: Ignitor = analogDefault,
        phase: Ignitor? = null,
    ): Ignitor = ImpulseIgnitor(freq = freq, analog = analog, phaseIn = phase?.let { PhaseOffset(it) })

    private class ImpulseIgnitor(
        private val freq: Ignitor,
        private val analog: Ignitor,
        private val phaseIn: PhaseOffset?,
    ) : Ignitor {
        private var phase: Double = 0.0
        private var lastPhase: Double = Double.POSITIVE_INFINITY

        /** Built with the node, seeded at the first block (see [seedAnalogDrift]). */
        private val drift = AnalogDrift()
        private var driftSeeded: Boolean = false
        private var started: Boolean = false

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            if (!driftSeeded) {
                driftSeeded = true
                seedAnalogDrift(drift = drift, analog = analog, freqHz = actualFreq, ctx = ctx)
            }

            val d = drift
            val phaseInc = TWO_PI * actualFreq / ctx.sampleRateD
            val phaseMod = ctx.phaseMod
            val end = ctx.windowEnd

            if (phaseIn != null) {
                if (!phaseIn.isSignal) {
                    val shift = phaseIn.blockDelta(actualFreq)

                    shiftBy(shift)
                }

                if (phaseIn.isSignal) {
                    renderPhased(buffer = buffer, actualFreq = actualFreq, ctx = ctx, d = d, phaseInc = phaseInc, phaseIn = phaseIn)

                    return
                }
            }

            started = true

            if (d.active) {
                d.beginBlock()

                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                var m = d.blockStart * 1.0
                val dm = rampStep(from = m, to = d.blockEnd, frames = end - ctx.offset)

                if (phaseMod == null) {
                    for (i in ctx.offset until end) {
                        buffer[i] = if (phase < lastPhase) 1.0 else 0.0
                        lastPhase = phase
                        phase += phaseInc * m
                        m += dm
                        phase = phase.wrapPhase(TWO_PI)
                    }
                } else {
                    for (i in ctx.offset until end) {
                        buffer[i] = if (phase < lastPhase) 1.0 else 0.0
                        lastPhase = phase
                        phase += phaseInc * phaseMod[i] * m
                        m += dm
                        phase = phase.wrapPhase(TWO_PI)
                    }
                }
            } else {
                if (phaseMod == null) {
                    for (i in ctx.offset until end) {
                        buffer[i] = if (phase < lastPhase) 1.0 else 0.0
                        lastPhase = phase
                        phase += phaseInc
                        phase = phase.wrapPhase(TWO_PI)
                    }
                } else {
                    for (i in ctx.offset until end) {
                        buffer[i] = if (phase < lastPhase) 1.0 else 0.0
                        lastPhase = phase
                        phase += phaseInc * phaseMod[i]
                        phase = phase.wrapPhase(TWO_PI)
                    }
                }
            }
        }

        /**
         * A block-constant phase change of [shift] cycles, folded into the accumulator. The block's first sample is
         * then judged by the phased loop's own rule, so both paths agree: it spikes when the heard phase dropped by
         * more than half a cycle since the last sample (a forward pass through 0, the natural wrap and the step
         * together); a step back of less than half a cycle spikes nothing. At the very first block nothing has been
         * crossed yet: a start phase other than 0 does not spike.
         */
        private fun shiftBy(shift: Double) {
            if (shift == 0.0) {
                return
            }

            phase = (phase + shift * TWO_PI).wrapPhase(TWO_PI)

            if (!started) {
                lastPhase = phase

                return
            }

            // +inf is the plain loop's "spike on the next sample"; the phase itself, "no spike"
            lastPhase = if (lastPhase - phase > PI) Double.POSITIVE_INFINITY else phase
        }

        /**
         * The phased loop (a moving `phase` signal): the shifted phase `phase + offset` spikes where it passes 0
         * going forward, read as a drop of more than half a cycle between two samples (a step back of less than
         * half a cycle spikes nothing). The very first sample spikes only at exactly 0, the unshifted start. Where
         * the plain loop's rule (any drop spikes) differs: an increment above half a cycle per sample (above
         * Nyquist) or a backward pitch modulation spike there and not here, so an offset of 0 renders the plain
         * loop's bits only for a forward increment below Nyquist.
         */
        private fun renderPhased(
            buffer: AudioBuffer, actualFreq: Double, ctx: IgniteContext, d: AnalogDrift, phaseInc: Double, phaseIn: PhaseOffset,
        ) {
            val phaseMod = ctx.phaseMod
            val off = ctx.offset
            val end = ctx.windowEnd

            ctx.scratchBuffers.use { offsets ->
                phaseIn.render(offsets, actualFreq, ctx)

                var m = 1.0
                var dm = 0.0

                if (d.active) {
                    d.beginBlock()
                    // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                    m = d.blockStart * 1.0
                    dm = rampStep(from = m, to = d.blockEnd, frames = end - off)
                }

                for (i in off until end) {
                    var p = phase + offsets[i].wrapToUnitCycle() * TWO_PI

                    if (p >= TWO_PI) {
                        p -= TWO_PI
                    }

                    val spike = if (started) lastPhase - p > PI else p == 0.0

                    buffer[i] = if (spike) 1.0 else 0.0
                    started = true
                    lastPhase = p

                    var inc = phaseInc

                    if (phaseMod != null) {
                        inc *= phaseMod[i]
                    }

                    phase += inc * m
                    m += dm
                    phase = phase.wrapPhase(TWO_PI)
                }
            }
        }
    }

    /**
     * Rounded pulse with variable [duty] cycle (square / pulse), band-limited [WaveIgnitor], PWM-capable. Phase 0
     * is -1, the foot of the rising edge (rise, the high plateau up to `duty`, fall, the low plateau); [phase]
     * (null: none) see [PhaseOffset].
     */
    fun pulze(
        freq: Ignitor = FreqIgnitor,
        duty: Ignitor = dutyDefault,
        analog: Ignitor = analogDefault,
        flankSamples: Double = PULSE_MIN_FLANK_SAMPLES,
        riseFlank: Double = PULSE_RISE_FLANK,
        fallFlank: Double = PULSE_FALL_FLANK,
        phase: Ignitor? = null,
    ): Ignitor = WaveIgnitor(
        freq = freq, analog = analog, kind = WaveKind.PULSE, polarity = 1.0, flankSamples = flankSamples,
        duty = duty, riseFlank = riseFlank, fallFlank = fallFlank, phaseIn = phase?.let { PhaseOffset(it) },
    )

    /** Brown noise (random walk with leaky integrator). Deeper, rumbly character. */
    /**
     * Brown (random-walk) noise. [leak] is the per-sample white-leak `k` in `out = (out + k·white)/(1+k)`
     * (read control-rate). `leak = BROWN_LEAK_DEFAULT` reproduces the original `/1.02` walk byte-for-byte.
     */
    fun brownNoise(rng: Random, leak: Ignitor = brownLeakDefault): Ignitor = BrownNoiseIgnitor(rng = rng, leak = leak)

    private class BrownNoiseIgnitor(private val rng: Random, private val leak: Ignitor) : Ignitor {
        private var out: Double = 0.0

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val k = readParam(leak, freqHz, ctx).coerceAtLeast(0.0)   // real freqHz: ledger O6
            val denom = 1.0 + k
            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                val white = rng.nextDouble() * 2.0 - 1.0

                out = (out + k * white) / denom
                buffer[i] = out
            }
        }
    }

    /** Pink noise (1/f spectrum via Paul Kellet's IIR cascades). */
    fun pinkNoise(rng: Random): Ignitor = PinkNoiseIgnitor(rng)

    private class PinkNoiseIgnitor(private val rng: Random) : Ignitor {
        private var b0: Double = 0.0
        private var b1: Double = 0.0
        private var b2: Double = 0.0
        private var b3: Double = 0.0
        private var b4: Double = 0.0
        private var b5: Double = 0.0
        private var b6: Double = 0.0

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                val white = rng.nextDouble() * 2.0 - 1.0

                b0 = 0.99886 * b0 + white * 0.0555179
                b1 = 0.99332 * b1 + white * 0.0750759
                b2 = 0.96900 * b2 + white * 0.1538520
                b3 = 0.86650 * b3 + white * 0.3104856
                b4 = 0.55000 * b4 + white * 0.5329522
                b5 = -0.7616 * b5 - white * 0.0168980

                val pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362

                b6 = white * 0.115926
                buffer[i] = (pink * 0.11)
            }
        }
    }

    /** Perlin noise: smooth organic noise using 1D Perlin noise. Output range -1..1. */
    fun perlinNoise(
        rng: Random,
        rate: Ignitor = rateDefault,
        octaves: Ignitor = octavesDefault,
        persistence: Ignitor = persistenceDefault,
    ): Ignitor = PerlinNoiseIgnitor(rng = rng, rate = rate, octaves = octaves, persistence = persistence)

    private class PerlinNoiseIgnitor(
        rng: Random,
        private val rate: Ignitor,
        private val octaves: Ignitor,
        private val persistence: Ignitor,
    ) : Ignitor {
        private val noise = PerlinNoise(rng)
        private var pos: Double = rng.nextDouble() * 256.0

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            // control-rate: read rate/octaves/persistence once per block. readParam, not a raw
            // scratch render + index (ledger O7: the raw read is the E5-bypass shape, and it
            // forced three full scratch renders per block for three constants), and the REAL
            // freqHz (ledger O6).
            val step = readParam(rate, freqHz, ctx) * PERLIN_STEP
            val oct = readParam(octaves, freqHz, ctx).toInt().coerceIn(1, PERLIN_FBM_MAX_OCTAVES)
            val pers = readParam(persistence, freqHz, ctx)
            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                buffer[i] = noise.fbm(x = pos, octaves = oct, persistence = pers)
                pos += step
            }
        }
    }

    /** Berlin noise: piecewise-linear interpolated random noise, scaled to -1..1. */
    fun berlinNoise(
        rng: Random,
        rate: Ignitor = rateDefault,
        octaves: Ignitor = octavesDefault,
        persistence: Ignitor = persistenceDefault,
    ): Ignitor = BerlinNoiseIgnitor(rng = rng, rate = rate, octaves = octaves, persistence = persistence)

    private class BerlinNoiseIgnitor(
        rng: Random,
        private val rate: Ignitor,
        private val octaves: Ignitor,
        private val persistence: Ignitor,
    ) : Ignitor {
        private val noise = BerlinNoise(rng)
        private var pos: Double = rng.nextDouble() * 256.0

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            // readParam + real freqHz — see PerlinNoiseIgnitor (ledger O6/O7).
            val step = readParam(rate, freqHz, ctx) * PERLIN_STEP
            val oct = readParam(octaves, freqHz, ctx).toInt().coerceIn(1, PERLIN_FBM_MAX_OCTAVES)
            val pers = readParam(persistence, freqHz, ctx)
            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                // BerlinNoise outputs 0..1, scale to -1..1
                buffer[i] = (noise.fbm(t = pos, octaves = oct, persistence = pers) * 2.0 - 1.0)
                pos += step
            }
        }
    }

    /**
     * Dust: sparse random impulses. [density] 0.0..1.0 controls impulse rate; [maxRateHz] caps the rate.
     * [tail] shapes the impulse amplitude (`amp^tail`, 1 = uniform); [bipolar] (>0.5) gives random ±sign.
     * At the defaults (tail = 1, bipolar off) the inner loop is byte-identical to the original dust.
     */
    fun dust(
        rng: Random,
        density: Ignitor = densityDefault,
        tail: Ignitor = tailDefault,
        bipolar: Ignitor = bipolarDefault,
        maxRateHz: Double = 200.0,
    ): Ignitor = DustIgnitor(rng = rng, density = density, tail = tail, bipolar = bipolar, maxRateHz = maxRateHz)

    private class DustIgnitor(
        private val rng: Random,
        private val density: Ignitor,
        private val tail: Ignitor,
        private val bipolar: Ignitor,
        private val maxRateHz: Double,
    ) : Ignitor {
        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            // readParam + real freqHz — see PerlinNoiseIgnitor (ledger O6/O7).
            val d = readParam(density, freqHz, ctx).coerceIn(0.0, 1.0)
            val rateHz = d * maxRateHz
            val p = (rateHz / ctx.sampleRateD).coerceIn(0.0, 1.0)
            // control-rate knobs (no buffer fill for Constant/Param) — read once per block
            val k = readParam(tail, freqHz, ctx).coerceAtLeast(0.0)
            val bip = readParam(bipolar, freqHz, ctx) > 0.5
            val end = ctx.windowEnd

            if (!bip && k == 1.0) {
                // perf-neutral, byte-identical default
                for (i in ctx.offset until end) {
                    buffer[i] = if (rng.nextDouble() < p) rng.nextDouble() else 0.0
                }
            } else {
                for (i in ctx.offset until end) {
                    buffer[i] = if (rng.nextDouble() < p) {
                        if (bip) {
                            val a = rng.nextDouble() * 2.0 - 1.0 // [-1,1)

                            if (k == 1.0) a else sign(a) * abs(a).pow(k) // shape magnitude, keep sign
                        } else {
                            val a = rng.nextDouble() // [0,1)

                            if (k == 1.0) a else a.pow(k)
                        }
                    } else {
                        0.0
                    }
                }
            }
        }
    }

    /**
     * Crackle: a chaotic recurrence (SuperCollider's Crackle map), DC-blocked to bipolar pops.
     * `y[n] = |chaos·y[n-1] − y[n-2] − CRACKLE_C|`; [chaos] ≈1.0 sparse … 2.0 dense/noisy. Uses no PRNG
     * for the signal — the rng only seeds the map away from the (0,0) fixed point.
     */
    fun crackle(rng: Random, chaos: Ignitor = chaosDefault): Ignitor = CrackleIgnitor(rng, chaos)

    private class CrackleIgnitor(rng: Random, private val chaos: Ignitor) : Ignitor {
        // chaotic-map state, seeded away from the (0,0) fixed point so it starts oscillating
        private var y1 = 0.3 + rng.nextDouble() * 0.3
        private var y2 = 0.3 + rng.nextDouble() * 0.3

        // one-pole DC blocker: the map output is unipolar (abs), this recenters it to bipolar pops
        private var dcX = 0.0
        private var dcY = 0.0

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            // readParam + real freqHz — see PerlinNoiseIgnitor (ledger O6/O7).
            val a = readParam(chaos, freqHz, ctx).coerceIn(0.0, CRACKLE_CHAOS_MAX)
            val end = ctx.windowEnd

            for (i in ctx.offset until end) {
                var y0 = abs(a * y1 - y2 - CRACKLE_C)

                if (y0 != y0) { // NaN-guard: re-seed if the map ever diverges
                    y0 = 0.5
                }

                y2 = y1
                y1 = y0
                dcY = y0 - dcX + CRACKLE_DC_POLE * dcY
                dcX = y0
                buffer[i] = dcY
            }
        }
    }

    /**
     * Supersaw: a detuned stack of the analog-flyback saw shape (mono). Voice count is read lazily
     * from the [voices] Ignitor param on the first block. See [UnisonStackIgnitor].
     */
    fun superSawRaw(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        // Unison character — defaults are the SUPERSAW_* tuning constants; the DSL threads per-voice overrides.
        sideAtten: Double = SUPERSAW_SIDE_ATTEN,
        gainJitter: Double = SUPERSAW_GAIN_JITTER,
        spreadPower: Double = SUPERSAW_SPREAD_POWER,
        centerJitterScale: Double = SUPERSAW_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERSAW_PHASE_POOL,
        drawTries: Double = SUPERSAW_DRAW_TRIES,
        kMin: Double = SUPERSAW_K_MIN,
        kMax: Double = SUPERSAW_K_MAX,
        poolSize: Double = SUPERSAW_POOL_SIZE,
        refreshEvery: Double = SUPERSAW_REFRESH_EVERY,
        selection: String = SUPERSAW_SELECTION,
        warmup: Double = SUPERSAW_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = UnisonStackIgnitor(
        kind = StackKind.SAW,
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        polarity = 1.0,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower, centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
        resetSamples = SAW_RESET_SAMPLES, shapeMax = SAW_SHAPE_MAX,
    )

    /** Super Saw (mono). Alias of [superSawRaw]; the historic pitch-tracking HPF wrapper was removed. */
    fun superSaw(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        sideAtten: Double = SUPERSAW_SIDE_ATTEN,
        gainJitter: Double = SUPERSAW_GAIN_JITTER,
        spreadPower: Double = SUPERSAW_SPREAD_POWER,
        centerJitterScale: Double = SUPERSAW_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERSAW_PHASE_POOL,
        drawTries: Double = SUPERSAW_DRAW_TRIES,
        kMin: Double = SUPERSAW_K_MIN,
        kMax: Double = SUPERSAW_K_MAX,
        poolSize: Double = SUPERSAW_POOL_SIZE,
        refreshEvery: Double = SUPERSAW_REFRESH_EVERY,
        selection: String = SUPERSAW_SELECTION,
        warmup: Double = SUPERSAW_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = superSawRaw(
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower, centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phase = phase, countsAtBuild = countsAtBuild,
    )

    /** The voice shape of a [UnisonStackIgnitor]: the analog-flyback saw, the [waveTrapezoid] pulse, or a pure sine. */
    private enum class StackKind { SAW, PULSE, SINE }

    /**
     * The one engine behind every unison oscillator (supersaw, superramp, supersquare, supertri, supersine): a stack of
     * detuned voices summed to mono with the super-saw character: center-dominant [sideAtten] gains, per-voice
     * amplitude [gainJitter], per-voice drift lanes ([DriftLanes] blended by [analogSpread]), even [detune] spacing
     * (shaped by [spreadPower]) with the **gain-weighted mean detune removed** so the pitch centroid sits exactly on the
     * note. [polarity] flips the waveform and is baked into the voice gains (no per-sample sign flip).
     *
     * [kind] picks the voice shape, like [WaveIgnitor]'s [WaveKind]: [configureShape] sets it per voice (control rate,
     * from the detuned increment), and the voice loop renders it, the [waveTrapezoid] for SAW and PULSE, a sine for
     * SINE, each in a plain and a phased loop of its own (one small loop per method, which the JIT compiles whole).
     * Each loop runs its own drift prologue. One shared prologue that handed the ramp to the loops as arguments was
     * measured 20 to 25 percent slower on V8 for the phased loops (tidy-up step 11, review round 1): V8 did not inline
     * them, so the arguments arrived untyped. The ramp's start passes `* 1.0` (the V8 rule in `audio/ref/performance.md`:
     * a seed from a call result stays tagged and every `m += dm` can allocate a heap number, one per sample per voice
     * with drift: seen under a mixed profile, every oscillator warmed in one process, in 2 of 3 processes on the
     * development bundle and none after; on the production bundle in 2 of 3 in one sample and 0 of 3 in another; V8
     * allocation pass).
     * The shape knobs are read only by their kind: [resetSamples] and [shapeMax] by SAW, [duty], [riseFlank],
     * [fallFlank] and [flankSamples] by PULSE; SINE reads none. Voice count is read lazily from the [voices] param;
     * detune + shape are cached, recomputed only when freq / voice count / spread change.
     */
    private class UnisonStackIgnitor(
        private val kind: StackKind,
        private val freq: Ignitor,
        private val voices: Ignitor,
        private val detune: Ignitor,
        private val analog: Ignitor,
        private val analogSpread: Ignitor,
        private val rng: Random,
        private val polarity: Double,
        private val sideAtten: Double,
        private val gainJitter: Double,
        private val spreadPower: Double,
        private val centerJitterScale: Double,
        private val phasePool: Double,
        private val drawTries: Double,
        private val kMin: Double,
        private val kMax: Double,
        private val poolSize: Double,
        private val refreshEvery: Double,
        private val selection: String,
        private val warmup: Double,
        private val phasePools: PhasePools?,
        private val orbit: Int,
        private val phaseIn: PhaseOffset?,
        countsAtBuild: Boolean,
        private val resetSamples: Double = 0.0,
        private val shapeMax: Double = 0.0,
        private val duty: Double = 0.0,
        private val riseFlank: Double = 0.0,
        private val fallFlank: Double = 0.0,
        private val flankSamples: Double = 0.0,
    ) : Ignitor {
        /** `selection` parsed at build (`"name[:width[:outliers]]"`, see [parsePhasePoolSelection]) when the pool is
         *  on; voices with the pool OFF (the default) never pay the parse, and the render never touches the string. */
        private val parsedSelection: PhasePoolSelectionParsed? = if (phasePool > 0.5) parsePhasePoolSelection(selection) else null

        /** The live voice count: the first [v] entries of [voiceStates] are the voices. */
        private var v: Int = 0

        /**
         * The voices' states, built with the node at the count the build can read ([unisonCapacity]); a shrink keeps
         * them and a voice that comes back re-uses its state. Only a count past the array
         * grows it, at render (a `voices` signal the build could not read).
         */
        private var voiceStates: Array<WaveVoiceState> = Array(unisonCapacity(voices = voices, countsAtBuild = countsAtBuild)) { WaveVoiceState() }

        /** The base gain profile of the live count, written by [computeVoiceGains]; sized like [voiceStates]. */
        private var baseGains: DoubleArray = DoubleArray(voiceStates.size)

        /** The best candidate of the stateless banded draw ([selectBandedPhases]); built only when that draw can run. */
        private var bestPhases: DoubleArray = DoubleArray(if (phasePool > 0.5 && phasePools == null) voiceStates.size else 0)

        /**
         * One lane per voice plus the shared walk, built with the node when `analog` may be above 0 (see
         * [buildDriftLanes]); [drift] is the container once started, null while `analog` is 0, which is the fast path.
         */
        private val builtLanes: DriftLanes? = buildDriftLanes(analog = analog, analogSpread = analogSpread, lanes = voiceStates.size)
        private var drift: DriftLanes? = null
        private var analogLatched: Boolean = false

        // Detune-recompute cache keys (NaN forces recompute).
        private var lastFreq: Double = Double.NaN
        private var lastSpread: Double = Double.NaN

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            val newV = coerceUnisonVoices(readParam(voices, actualFreq, ctx))
            // Read every block, like the bank and the pluck read theirs: a modulated subtree
            // advances once per block whether or not anything uses its value (ledger O2). Only the
            // DEPTH is latched, below.
            val analogAmt = readParam(analog, actualFreq, ctx)

            if (newV != v) {
                val oldV = v

                v = newV

                // Banded selection runs at note-on only (none → v); mid-note voice-count changes
                // keep plain random phases for added voices, and with the pool off the rng stream
                // must stay identical to the legacy draw order (phases here, jitter in
                // computeVoiceGains) — the bypass guarantee is bit-exact.
                // DECIDED (ledger O5, under the O3 block-start decision): a `voices` signal whose
                // first observation is below 2 forfeits the pool for the whole note; one that
                // starts at 0 gets its pool serve at the first observed rise. Both are the
                // control-rate observation semantics, not bugs — do not re-judge.
                val banded = phasePool > 0.5 && oldV == 0 && v >= 2

                // Past the arrays built with the node: grow them (the one allocation left here).
                if (v > voiceStates.size) {
                    val old = voiceStates

                    voiceStates = Array(v) { i -> if (i < old.size) old[i] else WaveVoiceState() }
                    baseGains = DoubleArray(v)
                }

                // Surviving voices keep their state (and phase); the voices from the old count up are new, or back
                // after a shrink, and take a random start phase, in index order: lush (phase-0 is thin; even spacing
                // makes voice-count-dependent overtones). Innocent for tuning: a phase offset doesn't change
                // frequency, and `p += dt` is unbiased. A state back after a shrink needs no reset: every field the
                // stack reads is written before the next render (the phase here or by the banded selection, the
                // jitter by drawGainJitterFor, the gain by computeVoiceGains, dt and the shape by computeDetunes,
                // forced below), so it renders as a freshly built one (review round 1).
                for (i in oldV until v) {
                    if (!banded) {
                        voiceStates[i].phase = rng.nextDouble()
                    }
                }

                // DECIDED (maintainer, 2026-08-28): voice-count changes are OBSERVED AT BLOCK
                // START by design (control-rate observation; block-framing ledger O3) — review
                // rounds do not re-judge the timing. Guaranteed at a mid-note change instead:
                // SURVIVING voices keep their phase, their drift lane (the slow OU layer must not
                // re-seed to centre mid-note) and their gain-jitter draw; only indices that are NEW,
                // or that were dropped by a shrink and have come back, draw from the rng (ledger
                // O4). At note-on the old count is 0, so the draw order below is bit-identical to the
                // legacy path — the phase-pool bypass guarantee holds.

                // Drift depth is latched at the first block that sizes the stack, the way the plain
                // sine latches its own: a later `analog` read never re-depths a lane, and a voice
                // added mid-note draws its lane at the latched depth.
                if (!analogLatched) {
                    analogLatched = true

                    if (analogAmt > 0.0) {
                        drift = startDriftLanes(built = builtLanes, analog = analogAmt, ctx = ctx)
                    }
                }

                // A shrink drops those voices (a voice that comes back is rewritten whole, above),
                // so their lanes are retired too: a voice that comes back gets a fresh state AND a fresh
                // lane, which is how it attacks in tune again.
                drift?.retireLanes(v)
                drift?.ensureLanes(v)

                drawGainJitterFor(from = oldV)
                computeVoiceGains()

                // Gains BEFORE phases: the stateless path scores candidates against the note's
                // actual jittered gains (exact K, doc §9.3); the POOLED path serves an entry
                // pre-scored against the base profile (doc §3.6) — jitter stays per-note either way.
                if (banded) {
                    val pool = phasePools?.pool(
                        orbit = orbit, voices = v, sideAtten = sideAtten,
                        kMin = kMin, kMax = kMax,
                        drawTries = drawTries, poolSize = poolSize, refreshEvery = refreshEvery,
                        warmup = warmup,
                    )

                    // `banded` needs the pool on, so the selection was parsed at build.
                    val sel = parsedSelection

                    if (pool != null && sel != null) {
                        val entry = pool.next(mode = sel.mode, width = sel.width, outliers = sel.outliers)

                        for (n in 0 until v) {
                            voiceStates[n].phase = entry[n]
                        }
                    } else {
                        selectBandedPhases()
                    }
                }

                lastFreq = Double.NaN         // force detune/shape recompute
                lastSpread = Double.NaN
            }

            if (v <= 0) {
                buffer.fill(0.0, ctx.offset, ctx.windowEnd); return
            }

            val spread = readParam(detune, actualFreq, ctx)

            // Recompute per-voice detune increment + shape ONCE per (freq, spread).
            if (actualFreq != lastFreq || spread != lastSpread) {
                lastFreq = actualFreq
                lastSpread = spread
                computeDetunes(actualFreq = actualFreq, spread = spread, sr = ctx.sampleRateD)
            }

            // Voice-major: each voice runs its whole sample block in one call (register residency);
            // voice 0 writes the buffer, the rest accumulate. `pm` = phaseMod (vibrato/FM/pitch-env/
            // accelerate), null when unmodulated.
            val pm = ctx.phaseMod
            val off = ctx.offset
            val end = off + ctx.length
            val lanes = drift
            // Read outside the null check: a modulated subtree advances once per block whether or
            // not anything reads its value this block (ledger O2).
            val analogSpreadAmt = readParam(analogSpread, actualFreq, ctx)

            // The shared walk steps once for the whole block, BEFORE the voice loop, so every
            // voice's ramp blends the same shared move.
            if (lanes != null) {
                lanes.prepareBlock(analogSpreadAmt)
            }

            // The phase shifts the whole stack: every voice by the same fraction of its own cycle, so the spread of
            // start phases (random or the pool's) stays as drawn, and so does the pool's coherence K (a common
            // shift leaves |sum g e^(i 2 pi phi)| unchanged).
            if (phaseIn != null) {
                if (!phaseIn.isSignal) {
                    val shift = phaseIn.blockDelta(actualFreq)

                    if (shift != 0.0) {
                        for (n in 0 until v) {
                            voiceStates[n].phase = (voiceStates[n].phase + shift).wrapPhase(1.0)
                        }
                    }
                }

                if (phaseIn.isSignal) {
                    ctx.scratchBuffers.use { offsets ->
                        phaseIn.render(offsets, actualFreq, ctx)

                        for (n in 0 until v) {
                            when (kind) {
                                StackKind.SAW, StackKind.PULSE -> trapezoidLoopPhased(buffer = buffer, off = off, end = end, vs = voiceStates[n], n = n, first = n == 0, pm = pm, drift = lanes, offsets = offsets)
                                StackKind.SINE -> sineLoopPhased(buffer = buffer, off = off, end = end, vs = voiceStates[n], n = n, first = n == 0, pm = pm, drift = lanes, offsets = offsets)
                            }
                        }
                    }

                    return
                }
            }

            for (n in 0 until v) {
                when (kind) {
                    StackKind.SAW, StackKind.PULSE -> trapezoidLoop(buffer = buffer, off = off, end = end, vs = voiceStates[n], n = n, first = n == 0, pm = pm, drift = lanes)
                    StackKind.SINE -> sineLoop(buffer = buffer, off = off, end = end, vs = voiceStates[n], n = n, first = n == 0, pm = pm, drift = lanes)
                }
            }
        }

        /** Set the per-voice shape from its detuned per-sample increment [dt] (control rate). */
        private fun configureShape(vs: WaveVoiceState, dt: Double) {
            when (kind) {
                StackKind.SAW -> {
                    val rf = (resetSamples * dt).coerceAtMost(shapeMax)

                    // NaN-guard: a NaN frequency made the flyback NaN and every sample NaN (the trapezoid's fall
                    // branch). The phase is scrubbed to 0 by the wrap meanwhile, so any finite shape holds -1: a voice at a NaN
                    // frequency now carries a held -1 (DC through its envelope) where it used to be NaN, scrubbed to silence.
                    vs.setSawShape(if (rf != rf) shapeMax else rf)
                }

                // No NaN guard here, none needed: a NaN frequency gives a NaN flank floor, and `coerceAtLeast(NaN)`
                // keeps the flank, so the floor drops out and the voice holds its floor-free shape (`UnisonStackShapeSpec`).
                StackKind.PULSE -> vs.setPulseShape(duty = duty, riseFlank = riseFlank, fallFlank = fallFlank, floor = flankSamples * dt)

                // The sine carries no shape.
                StackKind.SINE -> Unit
            }
        }

        /**
         * Renders voice [n]'s [waveTrapezoid] block: writes the buffer when [first], else accumulates onto it. The drift
         * prologue advances lane [n] right before voice [n] renders, voice by voice in index order. The order inside
         * (the voice's fields and `safeWrap`, then the prologue, then the samples) is the one the loops had before step
         * 11; the other order measured 9 to 11 percent slower on V8 for the sine (review round 1).
         */
        private fun trapezoidLoop(
            buffer: AudioBuffer, off: Int, end: Int, vs: WaveVoiceState, n: Int, first: Boolean,
            pm: DoubleArray?, drift: DriftLanes?,
        ) {
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
            var phase = vs.phase * 1.0
            val dt = vs.dt
            // The one-subtract wrap keeps the class invariant phase in [0, 1) only while |inc| < 1. A
            // frequency past the sample rate in either sign, or a spread typed in cents (raw-Motor,
            // uncoerced), gives |dt| >= 1, and a drifting voice can hold a near-1 dt over the edge for
            // seconds (the slow drift layer is a 10 s walk). Then the phase escapes: the trapezoid parks on
            // its low plateau (positive dt) or rides the rise ramp without bound (negative dt), the
            // polynomial sine diverges (1.8e34 measured in review). Hoisted: dt is block-constant. NaN
            // and infinite dt take the safe branch, which wraps them to 0.
            val safeWrap = pm != null || drift != null || !(abs(dt) < 1.0)
            val gain = vs.gain
            val riseEnd = vs.riseEnd
            val highEnd = vs.highEnd
            val fallEnd = vs.fallEnd
            val riseSlope = vs.riseSlope
            val fallSlope = vs.fallSlope
            // The drift ramp for this voice and block: one add per sample (DriftLanes KDoc).
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(n)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                val s = waveTrapezoid(p = phase, riseEnd = riseEnd, highEnd = highEnd, fallEnd = fallEnd, riseSlope = riseSlope, fallSlope = fallSlope) * gain

                buffer[i] = if (first) s else buffer[i] + s

                var inc = dt * m

                if (pm != null) {
                    inc *= pm[i]
                }

                m += dm
                phase += inc
                // No phaseMod, no drift and |dt| < 1 ⇒ |inc| < 1 ⇒ one conditional subtract or add;
                // otherwise the safe wrap (see safeWrap above).
                phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
            }

            vs.phase = phase
        }

        /**
         * [trapezoidLoop] for a moving `phase` signal: reads the shape at `phase + offsets[i]` and advances `phase` alike.
         * Every voice reads the same [offsets] (rendered before the first lane advances), so the stack moves as one.
         */
        private fun trapezoidLoopPhased(
            buffer: AudioBuffer, off: Int, end: Int, vs: WaveVoiceState, n: Int, first: Boolean,
            pm: DoubleArray?, drift: DriftLanes?, offsets: AudioBuffer,
        ) {
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
            var phase = vs.phase * 1.0
            val dt = vs.dt
            // The safe wrap as in trapezoidLoop.
            val safeWrap = pm != null || drift != null || !(abs(dt) < 1.0)
            val gain = vs.gain
            val riseEnd = vs.riseEnd
            val highEnd = vs.highEnd
            val fallEnd = vs.fallEnd
            val riseSlope = vs.riseSlope
            val fallSlope = vs.fallSlope
            // The drift ramp for this voice and block: one add per sample (DriftLanes KDoc).
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(n)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                var p = phase + offsets[i].wrapToUnitCycle()

                if (p >= 1.0) {
                    p -= 1.0
                }

                val s = waveTrapezoid(p = p, riseEnd = riseEnd, highEnd = highEnd, fallEnd = fallEnd, riseSlope = riseSlope, fallSlope = fallSlope) * gain

                buffer[i] = if (first) s else buffer[i] + s

                var inc = dt * m

                if (pm != null) {
                    inc *= pm[i]
                }

                m += dm
                phase += inc
                phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
            }

            vs.phase = phase
        }

        /** Renders voice [n]'s sine block, as [trapezoidLoop] renders the trapezoid. */
        private fun sineLoop(
            buffer: AudioBuffer, off: Int, end: Int, vs: WaveVoiceState, n: Int, first: Boolean,
            pm: DoubleArray?, drift: DriftLanes?,
        ) {
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
            var phase = vs.phase * 1.0
            val dt = vs.dt
            // The safe wrap as in trapezoidLoop.
            val safeWrap = pm != null || drift != null || !(abs(dt) < 1.0)
            val gain = vs.gain
            // The drift ramp for this voice and block: one add per sample (DriftLanes KDoc).
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(n)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                val s = fastSin(phase * TWO_PI) * gain

                buffer[i] = if (first) s else buffer[i] + s

                var inc = dt * m

                if (pm != null) {
                    inc *= pm[i]
                }

                m += dm
                phase += inc
                phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
            }

            vs.phase = phase
        }

        /** [sineLoop] for a moving `phase` signal: reads the sine at `phase + offsets[i]`, advances `phase` alike. */
        private fun sineLoopPhased(
            buffer: AudioBuffer, off: Int, end: Int, vs: WaveVoiceState, n: Int, first: Boolean,
            pm: DoubleArray?, drift: DriftLanes?, offsets: AudioBuffer,
        ) {
            // `* 1.0`: a seed from a field must pass an arithmetic op on V8, or the loop can box the phase (audio/ref/performance.md).
            var phase = vs.phase * 1.0
            val dt = vs.dt
            // The safe wrap as in trapezoidLoop.
            val safeWrap = pm != null || drift != null || !(abs(dt) < 1.0)
            val gain = vs.gain
            // The drift ramp for this voice and block: one add per sample (DriftLanes KDoc).
            var m = 1.0
            var dm = 0.0

            if (drift != null) {
                drift.advanceLane(n)
                // `* 1.0`: a seed from a call or a field must pass an arithmetic op on V8, or `m += dm` boxes (audio/ref/performance.md).
                m = drift.blockStart * 1.0
                dm = rampStep(from = m, to = drift.blockEnd, frames = end - off)
            }

            for (i in off until end) {
                var p = phase + offsets[i].wrapToUnitCycle()

                if (p >= 1.0) {
                    p -= 1.0
                }

                val s = fastSin(p * TWO_PI) * gain

                buffer[i] = if (first) s else buffer[i] + s

                var inc = dt * m

                if (pm != null) {
                    inc *= pm[i]
                }

                m += dm
                phase += inc
                phase = phase.wrapPhaseFastOrSafe(period = 1.0, safe = safeWrap)
            }

            vs.phase = phase
        }

        /**
         * Center-dominant base gains × per-voice amplitude jitter, renormalized to sum 1, with
         * [polarity] folded in (negated for the ramp) so the hot loop needs no per-sample sign flip.
         *
         * The CENTER voice (detune ≈ 0 → carries the perceived pitch / the "ring") gets a **scaled-down**
         * share of the jitter ([SUPERSAW_CENTER_JITTER_SCALE]): a big jitter draw on it would weaken the
         * on-pitch fundamental and make that note "won't ring", but fully exempting it (scale 0) flattens
         * the liveliness. `0.0` = stable center, `1.0` = jittered like the sides. Sides always get full
         * jitter; phases are untouched (random), so this trades only ring-stability vs liveliness, not timbre.
         */
        /** Draws gain-jitter for indices [from] until `v` — new voices only at a mid-note change,
         *  everyone at note-on (from = 0), keeping the legacy draw order bit-exact there. */
        private fun drawGainJitterFor(from: Int) {
            for (n in from until v) {
                voiceStates[n].jitDraw = rng.nextDouble()
            }
        }

        private fun computeVoiceGains() {
            val base = baseGains

            superSawVoiceGainsInto(v = v, sideAtten = sideAtten, gains = base)

            val center = (v - 1) / 2
            var s = 0.0

            for (n in 0 until v) {
                val scale = if (n == center) centerJitterScale else 1.0
                val jit = 1.0 + (voiceStates[n].jitDraw - 0.5) * 2.0 * gainJitter * scale
                val g = (base[n] * jit).coerceAtLeast(0.0)

                voiceStates[n].gain = g; s += g
            }

            if (s > 0.0) {
                val inv = polarity / s

                for (n in 0 until v) {
                    voiceStates[n].gain *= inv
                }
            }
        }

        /**
         * Banded best-of-M start-phase selection (KLANG-00144 (unison phase pool)): draw up to
         * [drawTries] candidate phase sets, score each's fundamental coherence
         * `K = |Σ gₙ·e^{i2πφₙ}| / |Σ gₙ|` against the note's actual (jittered, [polarity]-folded)
         * gains, and take the first candidate inside `[kMin, kMax]` — early exit is accept-reject
         * sampling, unbiased within the band — or the closest one if none lands. K is
         * frequency-invariant, so no pitch enters the score. Selection among honest random draws,
         * never alignment: K→1 would be phase-locked and thin, the lushness IS the incoherence.
         */
        private fun selectBandedPhases() {
            // Ceiling 64 is a compute bound on the note-on search loop (an unbounded count would
            // let a typo'd `.phasePool(drawTries = 1e9)` stall the render callback), not a sound clamp —
            // banded targeting saturates far below it.
            val tries = drawTries.toInt().coerceIn(1, 64)
            val lo = kMin.coerceIn(0.0, 1.0)
            val hi = kMax.coerceIn(lo, 1.0)
            var gsum = 0.0

            for (n in 0 until v) {
                gsum += voiceStates[n].gain
            }

            if (bestPhases.size < v) {
                bestPhases = DoubleArray(v) // past the size built with the node (a `voices` signal)
            }

            val best = bestPhases
            var bestDist = Double.MAX_VALUE

            @Suppress("unused")
            for (t in 0 until tries) {
                var re = 0.0
                var im = 0.0

                // Candidates are drawn straight into the voice states (phases are not read until
                // the first render); `best` snapshots the winner so far.
                for (n in 0 until v) {
                    val p = rng.nextDouble()

                    voiceStates[n].phase = p

                    val g = voiceStates[n].gain
                    val a = p * TWO_PI

                    re += g * cos(a)
                    im += g * sin(a)
                }

                // All gains share the polarity sign, so |gsum| is the coherent maximum.
                val k = if (gsum != 0.0) sqrt(re * re + im * im) / abs(gsum) else 1.0
                val dist = if (k < lo) lo - k else if (k > hi) k - hi else 0.0

                if (dist < bestDist) {
                    bestDist = dist

                    for (n in 0 until v) {
                        best[n] = voiceStates[n].phase
                    }
                }

                if (dist == 0.0) {
                    break
                }
            }

            for (n in 0 until v) {
                voiceStates[n].phase = best[n]
            }
        }

        /**
         * Per-voice detune increment + analog flyback shape, with the gain-weighted mean detune
         * removed so the perceived pitch centroid sits exactly on the note (anchors tuning despite
         * gain jitter, curve shaping, or voice-count parity). Works for either polarity (the sign
         * cancels in the weighted mean).
         */
        private fun computeDetunes(actualFreq: Double, spread: Double, sr: Double) {
            // Gain-weighted mean detune (two cheap passes — avoids a per-call array allocation).
            var wsum = 0.0
            var gsum = 0.0

            for (n in 0 until v) {
                val g = voiceStates[n].gain

                wsum += getUnisonDetune(unison = v, detune = spread, voiceIndex = n, spreadPower = spreadPower) * g; gsum += g
            }

            val mean = if (gsum != 0.0) wsum / gsum else 0.0

            for (n in 0 until v) {
                val vs = voiceStates[n]

                vs.dt = actualFreq * (getUnisonDetune(unison = v, detune = spread, voiceIndex = n, spreadPower = spreadPower) - mean).semitones() / sr
                configureShape(vs, vs.dt)
            }
        }
    }

    /**
     * Supersine: a detuned stack of pure sines (mono) on the shared [UnisonStackIgnitor] / super-saw
     * unison engine (center-dominant gains, per-voice drift, centroid-anchored detune; its own
     * `SUPERSINE_*` knobs, seeded to the super-saw values). Inherently band-limited, no anti-aliasing
     * needed. Voice count is read lazily from the [voices] Ignitor param on the first block.
     */
    fun superSine(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        sideAtten: Double = SUPERSINE_SIDE_ATTEN,
        gainJitter: Double = SUPERSINE_GAIN_JITTER,
        spreadPower: Double = SUPERSINE_SPREAD_POWER,
        centerJitterScale: Double = SUPERSINE_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERSINE_PHASE_POOL,
        drawTries: Double = SUPERSINE_DRAW_TRIES,
        kMin: Double = SUPERSINE_K_MIN,
        kMax: Double = SUPERSINE_K_MAX,
        poolSize: Double = SUPERSINE_POOL_SIZE,
        refreshEvery: Double = SUPERSINE_REFRESH_EVERY,
        selection: String = SUPERSINE_SELECTION,
        warmup: Double = SUPERSINE_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = UnisonStackIgnitor(
        kind = StackKind.SINE,
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        polarity = 1.0,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower,
        centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
    )

    /**
     * Supersquare: a detuned stack of the finite-slope pulse shape (duty 0.5, mono) on the shared
     * [UnisonStackIgnitor] / super-saw unison engine (center-dominant gains, per-voice drift,
     * centroid-anchored detune; its own `SUPERSQUARE_*` knobs, seeded to the super-saw values). Edges
     * are finite-slope flanks (no PolyBLEP), like the mono square. Voice count is read lazily from the
     * [voices] Ignitor param on the first block.
     */
    fun superSquare(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        sideAtten: Double = SUPERSQUARE_SIDE_ATTEN,
        gainJitter: Double = SUPERSQUARE_GAIN_JITTER,
        spreadPower: Double = SUPERSQUARE_SPREAD_POWER,
        centerJitterScale: Double = SUPERSQUARE_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERSQUARE_PHASE_POOL,
        drawTries: Double = SUPERSQUARE_DRAW_TRIES,
        kMin: Double = SUPERSQUARE_K_MIN,
        kMax: Double = SUPERSQUARE_K_MAX,
        poolSize: Double = SUPERSQUARE_POOL_SIZE,
        refreshEvery: Double = SUPERSQUARE_REFRESH_EVERY,
        selection: String = SUPERSQUARE_SELECTION,
        warmup: Double = SUPERSQUARE_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = UnisonStackIgnitor(
        kind = StackKind.PULSE,
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        polarity = 1.0,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower,
        centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
        duty = 0.5, riseFlank = PULSE_RISE_FLANK, fallFlank = PULSE_FALL_FLANK, flankSamples = PULSE_MIN_FLANK_SAMPLES,
    )

    /**
     * Supertri: a detuned stack of triangles (mono) on the shared [UnisonStackIgnitor] / super-saw
     * unison engine — the pulse shape with fully-open flanks (1.0/1.0, duty 0.5); its own `SUPERTRI_*`
     * knobs, seeded to the super-saw values. Piecewise linear, inherently band-limited. Voice count is
     * read lazily from the [voices] Ignitor param on the first block.
     */
    fun superTri(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        sideAtten: Double = SUPERTRI_SIDE_ATTEN,
        gainJitter: Double = SUPERTRI_GAIN_JITTER,
        spreadPower: Double = SUPERTRI_SPREAD_POWER,
        centerJitterScale: Double = SUPERTRI_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERTRI_PHASE_POOL,
        drawTries: Double = SUPERTRI_DRAW_TRIES,
        kMin: Double = SUPERTRI_K_MIN,
        kMax: Double = SUPERTRI_K_MAX,
        poolSize: Double = SUPERTRI_POOL_SIZE,
        refreshEvery: Double = SUPERTRI_REFRESH_EVERY,
        selection: String = SUPERTRI_SELECTION,
        warmup: Double = SUPERTRI_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = UnisonStackIgnitor(
        kind = StackKind.PULSE,
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        polarity = 1.0,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower,
        centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
        duty = 0.5, riseFlank = 1.0, fallFlank = 1.0, flankSamples = PULSE_MIN_FLANK_SAMPLES,
    )

    /**
     * Superramp: a detuned stack of the analog saw shape, **negated** (the mirror of [superSaw]).
     * Shares [UnisonStackIgnitor] (the SAW kind) with `polarity = −1`, the `RAMP_*` shape and its own
     * `SUPERRAMP_*` unison knobs (seeded to the super-saw values; change them to diverge). Voice
     * count is read lazily from the [voices] Ignitor param on the first block.
     */
    fun superRamp(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        sideAtten: Double = SUPERRAMP_SIDE_ATTEN,
        gainJitter: Double = SUPERRAMP_GAIN_JITTER,
        spreadPower: Double = SUPERRAMP_SPREAD_POWER,
        centerJitterScale: Double = SUPERRAMP_CENTER_JITTER_SCALE,
        phasePool: Double = SUPERRAMP_PHASE_POOL,
        drawTries: Double = SUPERRAMP_DRAW_TRIES,
        kMin: Double = SUPERRAMP_K_MIN,
        kMax: Double = SUPERRAMP_K_MAX,
        poolSize: Double = SUPERRAMP_POOL_SIZE,
        refreshEvery: Double = SUPERRAMP_REFRESH_EVERY,
        selection: String = SUPERRAMP_SELECTION,
        warmup: Double = SUPERRAMP_WARMUP,
        phasePools: PhasePools? = null,
        orbit: Int = 0,
        phase: Ignitor? = null,
        countsAtBuild: Boolean = false,
    ): Ignitor = UnisonStackIgnitor(
        kind = StackKind.SAW,
        freq = freq, voices = voices, detune = detune, analog = analog, analogSpread = analogSpread, rng = rng,
        polarity = -1.0,
        sideAtten = sideAtten, gainJitter = gainJitter, spreadPower = spreadPower,
        centerJitterScale = centerJitterScale,
        phasePool = phasePool, drawTries = drawTries, kMin = kMin, kMax = kMax,
        poolSize = poolSize, refreshEvery = refreshEvery, selection = selection, warmup = warmup,
        phasePools = phasePools, orbit = orbit, phaseIn = phase?.let { PhaseOffset(it) }, countsAtBuild = countsAtBuild,
        resetSamples = RAMP_RESET_SAMPLES, shapeMax = RAMP_SHAPE_MAX,
    )

    /**
     * Karplus-Strong plucked string synthesis via noise-burst-excited delay line with filtered feedback.
     * Extended with pick position modeling and allpass stiffness filtering. One [KarplusString].
     */
    fun karplusStrong(
        freq: Ignitor = FreqIgnitor,
        feedback: Ignitor = feedbackDefault,
        brightness: Ignitor = brightnessDefault,
        pickPosition: Ignitor = pickPositionDefault,
        stiffness: Ignitor = stiffnessDefault,
        analog: Ignitor = analogDefault,
        rng: Random,
    ): Ignitor = KarplusStrongIgnitor(freq = freq, feedback = feedback, brightness = brightness, pickPosition = pickPosition, stiffness = stiffness, analog = analog, rng = rng)

    private class KarplusStrongIgnitor(
        private val freq: Ignitor,
        private val feedback: Ignitor,
        private val brightness: Ignitor,
        private val pickPosition: Ignitor,
        private val stiffness: Ignitor,
        private val analog: Ignitor,
        private val rng: Random,
    ) : Ignitor {
        private val string = KarplusString()

        /** Built with the node, seeded at the first block (see [seedAnalogDrift]). */
        private val drift = AnalogDrift()
        private var driftSeeded: Boolean = false

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            if (!driftSeeded) {
                driftSeeded = true
                seedAnalogDrift(drift = drift, analog = analog, freqHz = actualFreq, ctx = ctx)
            }

            val d = drift
            val feedbackVal = readParam(feedback, actualFreq, ctx)
            val brightnessVal = readParam(brightness, actualFreq, ctx)
            val stiffnessVal = readParam(stiffness, actualFreq, ctx)
            val end = ctx.windowEnd
            val baseDelay = KarplusString.baseDelayOf(sampleRate = ctx.sampleRateD, freqHz = actualFreq)

            // The pick position is read once, at the pluck.
            if (!string.excited) {
                string.excited = true
                string.excite(baseDelay = baseDelay, pickPos = readParam(pickPosition, actualFreq, ctx), rng = rng)
            }

            var m = 1.0
            var dm = 0.0
            val hasDrift = d.active

            if (hasDrift) {
                d.beginBlock()
                m = d.blockStart
                dm = rampStep(from = m, to = d.blockEnd, frames = end - ctx.offset)
            }

            string.render(
                buffer = buffer, from = ctx.offset, to = end, baseDelay = baseDelay, phaseMod = ctx.phaseMod,
                hasDrift = hasDrift, driftStart = m, driftStep = dm,
                lpAlpha = KarplusString.lpAlphaOf(brightnessVal), hasStiffness = KarplusString.hasStiffnessOf(stiffnessVal),
                apCoeff = KarplusString.apCoeffOf(stiffnessVal), feedback = feedbackVal, gain = 1.0, accumulate = false,
            )
        }
    }

    /**
     * Super Karplus-Strong: multiple detuned plucked strings summed together.
     * Each string has independent noise excitation and its own drift lane, creating rich evolving
     * shimmer; [analogSpread] blends those lanes towards one shared walk for the whole instrument.
     * Voice count is read lazily from the [voices] Ignitor param on the first block. One [KarplusString] per voice.
     */
    fun superKarplusStrong(
        freq: Ignitor = FreqIgnitor,
        voices: Ignitor = voicesDefault,
        detune: Ignitor = detuneDefault,
        feedback: Ignitor = feedbackDefault,
        brightness: Ignitor = brightnessDefault,
        pickPosition: Ignitor = pickPositionDefault,
        stiffness: Ignitor = stiffnessDefault,
        analog: Ignitor = analogDefault,
        analogSpread: Ignitor = analogSpreadDefault,
        rng: Random,
        countsAtBuild: Boolean = false,
    ): Ignitor = SuperKarplusStrongIgnitor(
        freq = freq, voices = voices, detune = detune, feedback = feedback, brightness = brightness, pickPosition = pickPosition, stiffness = stiffness, analog = analog, analogSpread = analogSpread, rng = rng,
        countsAtBuild = countsAtBuild,
    )

    private class SuperKarplusStrongIgnitor(
        private val freq: Ignitor,
        private val voices: Ignitor,
        private val detune: Ignitor,
        private val feedback: Ignitor,
        private val brightness: Ignitor,
        private val pickPosition: Ignitor,
        private val stiffness: Ignitor,
        private val analog: Ignitor,
        private val analogSpread: Ignitor,
        private val rng: Random,
        countsAtBuild: Boolean,
    ) : Ignitor {
        private var v: Int = 0
        private var voiceGain: Double = 0.0

        /**
         * The strings, each with its delay line, built with the node at the count the build can read
         * ([unisonCapacity]); only a count past them grows the array, at render (a `voices` signal the build could
         * not read). A shrink keeps them all.
         */
        private var strings: Array<KarplusString> = Array(unisonCapacity(voices = voices, countsAtBuild = countsAtBuild)) { KarplusString() }

        /**
         * One lane per string plus the shared walk, built with the node when `analog` may be above 0 (see
         * [buildDriftLanes]); [drift] is the container once started, null while `analog` is 0, which is the fast path.
         */
        private val builtLanes: DriftLanes? = buildDriftLanes(analog = analog, analogSpread = analogSpread, lanes = strings.size)
        private var drift: DriftLanes? = null
        private var analogLatched: Boolean = false

        /**
         * Each string's base delay, [KarplusString.baseDelayOf] at the note's frequency times the string's unison
         * detune, for the first [v] strings; sized with [strings]. Computed by [computeBaseDelays] from [delayInputs]
         * and [delayInputsCount], and again only when one of them moves ([sameDelayInput]), so a held note reads one
         * element per string per block.
         *
         * A CACHE ON PURPOSE (engine follow-up 4, 2026-10-19; `audio/ref/performance.md`, "a non-integral double
         * handed to a function that is not inlined"). The string loop computed `getUnisonDetune`, `semitones()` and
         * [KarplusString.baseDelayOf] per string per block, each a call that takes and returns a double. In some V8
         * processes this `generate` inlined [DriftLanes.ensureLanes] and left `getUnisonDetune` out (it was then
         * compiled on its own in 3 of 3 high processes traced and in none of 77 low ones, where `ensureLanes` was the
         * call instead), so the detune and the result crossed as heap numbers, about two per string per block:
         * `spluck-a`'s rare tail, 288 to 460 B per block where a healthy process reads 15 to 50. With the cache nothing
         * that takes or returns a double runs per string on a held note, whatever V8 inlines.
         *
         * Exact: the values are the loop's, the same function of the same inputs, computed the same way; a NaN input
         * never matches, so it is recomputed every block as before. No test can pin the boxes: this KDoc is the guard.
         * Do not move the computation back into the string loop.
         */
        private var baseDelays: DoubleArray = DoubleArray(strings.size)

        /**
         * The inputs [baseDelays] was computed from, at [DELAY_FREQ] (the note's frequency), [DELAY_SPREAD] (the
         * detune) and [DELAY_SAMPLE_RATE]; NaN until the first computation. An array, not double fields, so a store
         * cannot box (engine follow-up 7). The sample rate is stored for [computeBaseDelays], never compared: a node
         * lives in one [IgniteContext], whose rate does not change (one context per voice), so a compare could never
         * fail and no test could tell it apart (review round 1, 2026-10-20).
         */
        private val delayInputs: DoubleArray = doubleArrayOf(Double.NaN, Double.NaN, Double.NaN)

        /** The string count [baseDelays] was computed for; -1 until the first computation. */
        private var delayInputsCount: Int = -1

        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            val actualFreq = resolveFreq(freq, freqHz, ctx)

            // readParam, NEVER a raw scratch render + index (ledger O1): on a zero-length terminal
            // window generate() writes nothing and the index read returns stale cross-voice pool
            // residue — which used to size the string array below.
            val newV = coerceUnisonVoices(readParam(voices, actualFreq, ctx))

            if (newV != v) {
                // DECIDED (maintainer, 2026-08-28): voice-count changes are OBSERVED AT BLOCK
                // START by design — `voices` is a control-rate parameter and its transitions land
                // on the block grid like every other control-rate observation (block-framing
                // ledger O3). Review rounds do not re-judge the timing. Guaranteed here instead:
                // capacity is KEPT on shrink (no realloc, no audio-thread allocation on regrow up
                // to the note's max), and a regrown voice RE-PLUCKS on purpose — a new string is a
                // new pluck.
                val oldV = v

                v = newV
                voiceGain = if (v > 0) 1.0 / v.toDouble() else 0.0

                if (v > strings.size) {
                    val old = strings

                    strings = Array(v) { i -> if (i < old.size) old[i] else KarplusString() }
                    baseDelays = DoubleArray(v)
                }

                // A regrown string RE-PLUCKS (the DECIDED note above), so it gets a fresh lane
                // too, exactly like a returning stack voice: retire from the new count up and the
                // string loop's ensureLanes rebuilds what comes back.
                drift?.retireLanes(v)

                for (i in oldV until v) {
                    strings[i].excited = false
                }
            }

            if (v <= 0) {
                buffer.fill(0.0, ctx.offset, ctx.windowEnd)
                return
            }

            // Read control-rate params once per block
            val spread = readParam(detune, actualFreq, ctx)
            val feedbackVal = readParam(feedback, actualFreq, ctx)
            val brightnessVal = readParam(brightness, actualFreq, ctx)
            val stiffnessVal = readParam(stiffness, actualFreq, ctx)

            val lpAlpha = KarplusString.lpAlphaOf(brightnessVal)
            val hasStiffness = KarplusString.hasStiffnessOf(stiffnessVal)
            val apCoeff = KarplusString.apCoeffOf(stiffnessVal)

            val sr = ctx.sampleRateD
            val phaseMod = ctx.phaseMod
            val end = ctx.windowEnd

            // Each string's base delay, computed again only when an input moves (see [baseDelays]).
            val delaysHold = delayInputsCount == v &&
                sameDelayInput(stored = delayInputs[DELAY_FREQ], arriving = actualFreq) &&
                sameDelayInput(stored = delayInputs[DELAY_SPREAD], arriving = spread)

            if (!delaysHold) {
                delayInputs[DELAY_FREQ] = actualFreq
                delayInputs[DELAY_SPREAD] = spread
                delayInputs[DELAY_SAMPLE_RATE] = sr
                delayInputsCount = v
                computeBaseDelays()
            }

            // O2: param reads hoisted OUT of the per-string loop — a modulated subtree must
            // advance once per block, not v times, and every string samples the same value.
            val analogAmt = readParam(analog, actualFreq, ctx)
            val analogSpreadAmt = readParam(analogSpread, actualFreq, ctx)
            val pickPosVal = readParam(pickPosition, actualFreq, ctx)

            // Drift depth is latched at the FIRST block, like the plain sine and the unison stacks:
            // an `analog` that starts at 0 leaves this instrument dry for the whole note, and no
            // lane is ever built inside a later block's callback. The read above still happens every
            // block, so a modulated subtree keeps advancing (ledger O2); only the depth is latched.
            if (!analogLatched) {
                analogLatched = true

                if (analogAmt > 0.0) {
                    drift = startDriftLanes(built = builtLanes, analog = analogAmt, ctx = ctx)
                }
            }

            val lanes = drift

            // The shared walk runs once for the whole block, BEFORE the string loop, so every
            // string's ramp blends the same shared move.
            if (lanes != null) {
                lanes.prepareBlock(analogSpreadAmt)
            }

            for (n in 0 until v) {
                val s = strings[n]

                // Drawn just before this string's excitation, the order the strings had when each
                // one held its own lane; a string that came back after a shrink draws a fresh one.
                lanes?.ensureLanes(n + 1)

                val baseDelay = baseDelays[n]

                // Excite each string independently; a string that comes back keeps its filter state.
                if (!s.excited) {
                    s.excited = true
                    s.excite(baseDelay = baseDelay, pickPos = pickPosVal, rng = rng)
                }

                // Hoisted once per string: everything the blend reads is constant for the block.
                var m = 1.0
                var dm = 0.0

                if (lanes != null) {
                    lanes.advanceLane(n)
                    m = lanes.blockStart
                    dm = rampStep(from = m, to = lanes.blockEnd, frames = end - ctx.offset)
                }

                // The first string writes the buffer, the others add to it.
                s.render(
                    buffer = buffer, from = ctx.offset, to = end, baseDelay = baseDelay, phaseMod = phaseMod,
                    hasDrift = lanes != null, driftStart = m, driftStep = dm,
                    lpAlpha = lpAlpha, hasStiffness = hasStiffness, apCoeff = apCoeff, feedback = feedbackVal,
                    gain = voiceGain, accumulate = n != 0,
                )
            }
        }

        /**
         * Fills [baseDelays] for the first [v] strings from [delayInputs], exactly as the string loop computed them
         * per block before engine follow-up 4. Takes no double and returns none (see [baseDelays]).
         */
        private fun computeBaseDelays() {
            val actualFreq = delayInputs[DELAY_FREQ]
            val spread = delayInputs[DELAY_SPREAD]
            val sr = delayInputs[DELAY_SAMPLE_RATE]

            for (n in 0 until v) {
                val detuneSemitones = getUnisonDetune(unison = v, detune = spread, voiceIndex = n)
                val detunedFreq = actualFreq * detuneSemitones.semitones()

                baseDelays[n] = KarplusString.baseDelayOf(sampleRate = sr, freqHz = detunedFreq)
            }
        }

        /**
         * Whether an input of [baseDelays] holds: the same number, with `-0.0` and `0.0` told apart (a frequency of
         * `-0.0` reads another delay than `0.0`). A NaN never holds, so a NaN input is computed again every block, as
         * the loop did, unlike `sameKnob`, which takes two NaNs for one setting.
         */
        @Suppress("NOTHING_TO_INLINE")
        private inline fun sameDelayInput(stored: Double, arriving: Double): Boolean =
            stored == arriving && (stored != 0.0 || 1.0 / stored == 1.0 / arriving)

        private companion object {
            /** The slots of [delayInputs]. */
            const val DELAY_FREQ: Int = 0
            const val DELAY_SPREAD: Int = 1
            const val DELAY_SAMPLE_RATE: Int = 2
        }
    }

    /** Silence: fills buffer with zeros. */
    fun silence(): Ignitor = SilenceIgnitor

    private object SilenceIgnitor : Ignitor {
        override fun generate(buffer: AudioBuffer, freqHz: Double, ctx: IgniteContext) {
            buffer.fill(0.0, ctx.offset, ctx.windowEnd)
        }
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // Analog drift helper
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Read a control-rate parameter once per block: the scalar directly when the param is block-constant
     * (FreqIgnitor / ParamIgnitor / ConstantIgnitor and pointwise combinators over them, no scratch
     * buffer), otherwise one rendered sample. See [Ignitor.blockStartValue]. Reads exactly what
     * [Ignitor.blockStartValue] reads, for every node: the flag ([Ignitor.isBlockConstant]) decides alone, a true
     * one reads the primitive [Ignitor.controlRateValue], a false one renders through the member.
     *
     * Kotlin `inline` ON PURPOSE (engine follow-ups 8 and 12, 2026-10-10; `audio/ref/performance.md`, "a shared
     * one-line forwarding helper is one megamorphic call site"). As a plain function its one call into the node was
     * ONE call site for every knob read of the whole engine: on V8 it went megamorphic, was never inlined, and its
     * double result became a heap number per read whenever it was not a small integer (4 to 6 per classic voice
     * block, about 60 to 100 bytes). Inline, every caller owns its own call site for the scalar, monomorphic in
     * practice, which V8 inlines into the caller, so the double never crosses a call. No test can pin it (the sound
     * is the same either way): this KDoc is the guard. Do not turn it back into a plain function, and read a knob
     * through this, not through [Ignitor.blockStartValue] directly.
     */
    @Suppress("NOTHING_TO_INLINE")
    internal inline fun readParam(param: Ignitor, freqHz: Double, ctx: IgniteContext): Double =
        if (param.isBlockConstant) {
            param.controlRateValue(freqHz)
        } else {
            param.blockStartValue(freqHz, ctx)
        }

    /** Resolve the effective oscillator frequency ([readParam] over [freq]; [FreqIgnitor] → the voice note). */
    internal fun resolveFreq(freq: Ignitor, voiceFreqHz: Double, ctx: IgniteContext): Double =
        readParam(freq, voiceFreqHz, ctx)

    /**
     * Seeds [drift] from the [analog] param on the first block (read once, control rate). The lane is built with the
     * node; its draws stay here, at the first block, where they have always been (tidy-up step 10).
     */
    internal fun seedAnalogDrift(drift: AnalogDrift, analog: Ignitor, freqHz: Double, ctx: IgniteContext) {
        drift.seed(analog = readParam(analog, freqHz, ctx), stepRate = ctx.driftStepRate, rng = ctx.random)
    }

    /**
     * A param's value at BUILD, for sizing only (tidy-up step 10): its block-constant value, read at freq 0, or null
     * for a signal. Nothing reads this for sound: a node sizes its storage from it and still reads the param at its
     * first block. A COUNT is read here only when the build vouched that it does not read the note's frequency
     * (`countsAtBuild`), so the value is the one the first block reads: a count that falls with pitch, read at freq
     * 0, built up to the cap of 64 strings (review round 1). `analog` and `analogSpread` are read here as a yes or no
     * ([mayDrift], [mayShare]); a frequency-dependent one can guess wrong, which costs one lane or container nobody
     * uses, or the construction at the first block that every node did before this step.
     */
    private fun sizingValueOrNull(param: Ignitor): Double? = param.controlRateValueOrNull(freqHz = 0.0)

    /**
     * Whether [analog] may be above 0 at the first block, as far as the build can tell ([sizingValueOrNull]): drift
     * storage is built only then. A wrong guess costs storage nobody uses, or the old construction at the first block.
     */
    private fun mayDrift(analog: Ignitor): Boolean {
        val a = sizingValueOrNull(analog)

        return a == null || a > 0.0
    }

    /** Whether [analogSpread] may be below 1 at some block, as far as the build can tell: the shared walk is built only then. */
    private fun mayShare(analogSpread: Ignitor): Boolean {
        val s = sizingValueOrNull(analogSpread)

        return s == null || s < 1.0
    }

    /** The drift container a multi-voice node builds with itself: [lanes] own lanes, or none at all when [analog] is known to be 0. */
    private fun buildDriftLanes(analog: Ignitor, analogSpread: Ignitor, lanes: Int): DriftLanes? =
        if (mayDrift(analog)) DriftLanes(capacity = lanes, sharedLane = mayShare(analogSpread)) else null

    /**
     * Starts the container [built] with the node at the first block whose depth [analog] is above 0, or one made here
     * when the build ruled drift out (a depth the build read as 0). Draws the shared seed (see [DriftLanes.start]).
     */
    private fun startDriftLanes(built: DriftLanes?, analog: Double, ctx: IgniteContext): DriftLanes {
        val lanes = built ?: DriftLanes(capacity = 0, sharedLane = false)

        lanes.start(analog = analog, stepRate = ctx.driftStepRate, rng = ctx.random)

        return lanes
    }

    /**
     * The unison voice count [voices] will read at the first block, when [countsAtBuild] says it does not read the
     * note's frequency; 0 for a signal or a count that does (it grows at the first block, as before tidy-up step 10).
     */
    private fun unisonCapacity(voices: Ignitor, countsAtBuild: Boolean): Int {
        if (!countsAtBuild) {
            return 0
        }

        val v = sizingValueOrNull(voices) ?: return 0

        return coerceUnisonVoices(v)
    }

    // ═════════════════════════════════════════════════════════════════════════════
    // Internal helpers
    // ═════════════════════════════════════════════════════════════════════════════

    // PERLIN_STEP and PERLIN_FBM_MAX_OCTAVES live in `_noise_laws.kt`, their one home in the module.

    // ═════════════════════════════════════════════════════════════════════════════
    // wrapPhase(), smallNumFastMod() live in `utils/phase_wrap.kt`; a semitone detune is
    // `common.math.semitones()`

    // ═════════════════════════════════════════════════════════════════════════════
    // Unison / supersaw helpers
    // ═════════════════════════════════════════════════════════════════════════════

    /**
     * Center-dominant unison gain profile for the super-saw. The least-detuned (center)
     * voices are loudest; the most-detuned (edge) voices form a quieter halo — the
     * JP-8000 / Szabo Super Saw character. Summing all voices flat (`1/v`) sounds uniform
     * and comb-filtered ("plastic"), and with random start phases an unlucky draw can sum
     * destructively so the note barely rings. A dominant center voice anchors the note
     * (always rings) while the quiet detuned halo only adds shimmer.
     *
     * Triangular falloff with distance-from-center, normalised to sum to 1 (same overall
     * level as the old flat `1/v`). [sideAtten] tunes the falloff: 0 = flat/equal, 1 = only the
     * center voice (defaults to [SUPERSAW_SIDE_ATTEN]; the super-ramp passes its own). Tune by ear.
     */
    internal fun superSawVoiceGains(v: Int, sideAtten: Double = SUPERSAW_SIDE_ATTEN): DoubleArray {
        val gains = DoubleArray(if (v > 0) v else 0)

        superSawVoiceGainsInto(v = v, sideAtten = sideAtten, gains = gains)

        return gains
    }

    /** [superSawVoiceGains] written into the first [v] entries of [gains] (at least [v] long), allocating nothing. */
    internal fun superSawVoiceGainsInto(v: Int, sideAtten: Double, gains: DoubleArray) {
        if (v <= 0) {
            return
        }

        if (v == 1) {
            gains[0] = 1.0
            return
        }

        val c = (v - 1) * 0.5            // center index (fractional)
        val halfSpan = c                 // > 0 for v >= 2
        var s = 0.0

        for (n in 0 until v) {
            val d = n - c
            val dn = (if (d < 0.0) -d else d) / halfSpan          // 0 at center .. 1 at edges
            val g = (1.0 - sideAtten * dn).coerceAtLeast(0.0)

            gains[n] = g
            s += g
        }

        val norm = if (s > 0.0) 1.0 / s else 0.0

        for (n in 0 until v) {
            gains[n] *= norm
        }
    }

    // Oscillator character constants (SAW_* / SUPERSAW_* / RAMP_* / SUPERRAMP_*) live in OscillatorTuning.kt.

    internal fun getUnisonDetune(
        unison: Int,
        detune: Double,
        voiceIndex: Int,
        spreadPower: Double = SUPERSAW_SPREAD_POWER,
    ): Double {
        if (unison < 2) {
            return 0.0
        }

        val a = -detune * 0.5
        val b = detune * 0.5
        var n = voiceIndex.toDouble() / (unison - 1).toDouble()   // 0..1 across the spread

        if (spreadPower != 1.0) {
            // Signed power around the center (0.5) keeps the spacing symmetric (no detuning).
            val x = n * 2.0 - 1.0                                 // -1..+1
            val sx = (if (x < 0.0) -1.0 else 1.0) * abs(x).pow(spreadPower)

            n = (sx + 1.0) * 0.5
        }

        return n * (b - a) + a
    }
}
